import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PageHeader from '../components/PageHeader';
import { getResources, getCalendarMonth, getTickets, getActiveCompanies, createTicketInAutoTask, addTimeEntry, addBatchTimeEntries, addCustomClientTimeEntry, updateTicket, addTicketNote, CompanyOption, getBidJobsForCompany, getDefaultBidJob, BidJob, updateCalendarTimeEntry, deleteCalendarTimeEntry, getTimeTechProjectsForCompany, getMileageEntries, createMileageEntry, getExpenseEntries, createExpenseEntry, getMileageEstimate, getSystemSetting, retryTimeTechSync, BatchTimeEntryResult } from '../services/api';
import { Resource, CalendarTimeEntry, CalendarMonthData, Ticket, TimeTechProject, MileageEntry, ExpenseEntry } from '../services/types';
import NewTicketModal from '../components/NewTicketModal';
import { useAuth } from '../contexts/AuthProvider';
import { useToast } from '../contexts/ToastProvider';
import { useAdminConfig } from '../contexts/AdminConfigProvider';
import ViewSelector, { ViewMode } from '../components/calendar/ViewSelector';
import DayView from '../components/calendar/DayView';
import WeekView from '../components/calendar/WeekView';
import YearView from '../components/calendar/YearView';
import ScheduleView from '../components/calendar/ScheduleView';
import CompareView from '../components/calendar/CompareView';
import ReconcileView from '../components/calendar/ReconcileView';
import MaintenanceView from '../components/calendar/MaintenanceView';
import { getWeekDates, getWorkWeekDates, formatDisplayDate, shortMonthNames } from '../components/calendar/calendarUtils';


/**
 * Normalize company name for consistent matching across AutoTask and TimeTech
 */
function normalizeCompanyName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[,\s]+Inc\.?$/i, ', Inc.')
    .replace(/[,\s]+LLC\.?$/i, ', LLC')
    .replace(/[,\s]+Ltd\.?$/i, ', Ltd.')
    .replace(/\.$/g, '');
}

/**
 * Determine TimeTech project based on bid-job type
 * - PCP bid-jobs (Proactive Checks) -> "Proactive Checks" project
 * - Other bid-jobs (Help Desk) -> "Help Desk" project
 * Returns { projectId, projectName } or null if no matching project found
 */
function getProjectForBidJob(
  bidJob: { bidJobNumber?: string } | null,
  projects: TimeTechProject[]
): { projectId: number; projectName: string } | null {
  if (!bidJob || !bidJob.bidJobNumber || projects.length === 0) {
    return null;
  }

  const isPCPBidJob = bidJob.bidJobNumber.toUpperCase().startsWith('PCP');
  const targetProjectName = isPCPBidJob ? 'Proactive Checks' : 'Help Desk';

  // Find project by name (case-insensitive)
  const matchingProject = projects.find(
    p => p.projectName.toLowerCase() === targetProjectName.toLowerCase() && p.isActive
  );

  if (matchingProject) {
    return {
      projectId: matchingProject.projectId,
      projectName: matchingProject.projectName,
    };
  }

  return null;
}

// Google Calendar-style solid colors for better visibility
const COMPANY_COLORS = [
  { bg: 'bg-blue-500', hex: '#3b82f6' },
  { bg: 'bg-emerald-500', hex: '#10b981' },
  { bg: 'bg-amber-500', hex: '#f59e0b' },
  { bg: 'bg-cyan-500', hex: '#06b6d4' },
  { bg: 'bg-rose-500', hex: '#f43f5e' },
  { bg: 'bg-teal-500', hex: '#14b8a6' },
  { bg: 'bg-orange-500', hex: '#f97316' },
  { bg: 'bg-indigo-500', hex: '#6366f1' },
  { bg: 'bg-purple-500', hex: '#a855f7' },
  { bg: 'bg-pink-500', hex: '#ec4899' },
  { bg: 'bg-lime-500', hex: '#84cc16' },
  { bg: 'bg-red-500', hex: '#ef4444' },
];

function getCompanyColor(companyName: string) {
  const normalized = normalizeCompanyName(companyName);
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
    hash = hash & hash;
  }
  return COMPANY_COLORS[Math.abs(hash) % COMPANY_COLORS.length];
}

function formatHours(h: number): string {
  return (h ?? 0).toFixed(2) + 'h';
}

const shortDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Entry height constants (in pixels)
const ENTRY_HEIGHT = 22; // pill height + spacing
const DAY_HEADER_HEIGHT = 28; // date row
const DAY_FOOTER_HEIGHT = 20; // +more and + button row
const MIN_VISIBLE_EVENTS = 2;
const MAX_VISIBLE_EVENTS_CAP = 3;

// Search filter interface
interface SearchFilters {
  query: string;
  dateFrom: string;
  dateTo: string;
  minHours: string;
  maxHours: string;
}

// Helper to get first and last day of a month
function getMonthDateRange(date: Date): { first: string; last: string } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const formatDate = (d: Date) => d.toISOString().split('T')[0];
  return { first: formatDate(firstDay), last: formatDate(lastDay) };
}

const emptyFilters: SearchFilters = {
  query: '',
  dateFrom: '',
  dateTo: '',
  minHours: '',
  maxHours: '',
};

/**
 * Time Entry Calendar Page - Google Calendar Style
 */
export default function Calendar() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('autotask-hub-calendar-view');
    return (saved as ViewMode) || 'month';
  });
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(() => {
    // Check calendar-specific viewing key first (admins viewing other techs)
    const calViewing = localStorage.getItem('autotask-hub-calendar-viewing');
    if (calViewing && !isNaN(parseInt(calViewing, 10))) return parseInt(calViewing, 10);
    // Fall back to user's own identity
    const saved = localStorage.getItem('autotask-hub-resource-id');
    return saved ? parseInt(saved, 10) : null;
  });

  // For admin users: can view by contractor name instead of resourceId
  const [viewingSelection, setViewingSelection] = useState<string>(() => {
    const calViewing = localStorage.getItem('autotask-hub-calendar-viewing');
    if (calViewing) return calViewing;
    const saved = localStorage.getItem('autotask-hub-resource-id');
    return saved || '';
  });

  // Admin config from shared context
  const { contractors, hiddenResourceIds, isAdmin: checkIsAdmin } = useAdminConfig();
  const isAdmin = checkIsAdmin(user?.email);

  // Calendar data from API
  const [monthData, setMonthData] = useState<CalendarMonthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mini calendar state (independent navigation)
  const [miniCalendarDate, setMiniCalendarDate] = useState(currentDate);

  // Sync mini calendar with main calendar
  useEffect(() => {
    setMiniCalendarDate(currentDate);
  }, [currentDate]);

  // Company filter - tracks companies user explicitly hid (empty = show all)
  const [hiddenCompanies, setHiddenCompanies] = useState<Set<string>>(new Set());
  // Tracks the explicit whitelist when user has filtered (null = no explicit filter, show all)
  const [explicitlySelected, setExplicitlySelected] = useState<Set<string> | null>(null);

  // Search state
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(emptyFilters);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeFilters, setActiveFilters] = useState<SearchFilters>(emptyFilters);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Entry popup state
  const [viewingEntry, setViewingEntry] = useState<CalendarTimeEntry | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);

  // Overflow popup state
  const [overflowPopup, setOverflowPopup] = useState<{ day: number; entries: CalendarTimeEntry[]; x: number; y: number } | null>(null);

  // Dynamic entry count based on available space
  const calendarGridRef = useRef<HTMLDivElement>(null);
  const [maxVisibleEvents, setMaxVisibleEvents] = useState(2);

  // Add entry form state
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [highlightedCompanyIndex, setHighlightedCompanyIndex] = useState(-1);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const companySearchRef = useRef<HTMLInputElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // Ticket selection state
  const [companyTickets, setCompanyTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isCreatingNewTicket, setIsCreatingNewTicket] = useState(false);

  // New ticket fields
  const [newTicketTitle, setNewTicketTitle] = useState('');
  const [newTicketQueue, setNewTicketQueue] = useState('Level I Support');
  const [newTicketPriority, setNewTicketPriority] = useState('Medium');
  const [autoCompleteTicket, setAutoCompleteTicket] = useState(true);

  // Existing ticket options
  const [closeExistingTicket, setCloseExistingTicket] = useState(false);

  // Batch ticket selection (multi-ticket mode)
  interface BatchTicketEntry {
    ticket: Ticket;
    hoursWorked: string;
    closeTicket: boolean;
    summaryNotes: string;
  }
  const [batchTickets, setBatchTickets] = useState<BatchTicketEntry[]>([]);
  const [batchResults, setBatchResults] = useState<BatchTimeEntryResult | null>(null);
  const [ticketSearchFilter, setTicketSearchFilter] = useState('');
  const [showClosedTickets, setShowClosedTickets] = useState(false);

  // Common form fields
  const [hoursWorked, setHoursWorked] = useState('');
  const [summaryNotes, setSummaryNotes] = useState('');

  // Bid-job state
  const [bidJobs, setBidJobs] = useState<BidJob[]>([]);
  const [selectedBidJob, setSelectedBidJob] = useState<BidJob | null>(null);
  const [loadingBidJobs, setLoadingBidJobs] = useState(false);

  // TimeTech projects for bid-job mapping (Help Desk / Proactive Checks)
  const [timetechProjects, setTimetechProjects] = useState<TimeTechProject[]>([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [modalDate, setModalDate] = useState<Date | null>(null);

  // Pending/Failed entries modal
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingModalType, setPendingModalType] = useState<'pending' | 'failed'>('pending');
  const [retryingEntries, setRetryingEntries] = useState<Set<string>>(new Set());

  // Edit entry state
  const [editingEntry, setEditingEntry] = useState<CalendarTimeEntry | null>(null);
  const [editHours, setEditHours] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editBidJobs, setEditBidJobs] = useState<BidJob[]>([]);
  const [editSelectedBidJob, setEditSelectedBidJob] = useState<BidJob | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Drag-and-drop state
  const [dragEntry, setDragEntry] = useState<CalendarTimeEntry | null>(null);
  const [dropTargetDay, setDropTargetDay] = useState<number | null>(null);
  // Week view drops onto a full date string (a week can straddle a month boundary)
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);
  const [dragActionModal, setDragActionModal] = useState<{ entry: CalendarTimeEntry; targetDate: string } | null>(null);
  const [dragActionLoading, setDragActionLoading] = useState(false);
  const [dragActionError, setDragActionError] = useState<string | null>(null);

  // Recurring template state

  // Entry type tabs for Add modal: time | mileage | expense
  const [entryType, setEntryType] = useState<'time' | 'mileage' | 'expense'>('time');

  // Mileage form state (Calendar standalone)
  const [calMileageStart, setCalMileageStart] = useState<string>('Office');
  const [calMileageDestination, setCalMileageDestination] = useState<string>('');
  const [calOdometerStart, setCalOdometerStart] = useState<string>('');
  const [calOdometerEnd, setCalOdometerEnd] = useState<string>('');
  const [calMileageItem, setCalMileageItem] = useState<string>('');
  const [calEstimatedMiles, setCalEstimatedMiles] = useState<number | null>(null);
  const [calMileageRate, setCalMileageRate] = useState<number>(0.725);

  // Expense form state (Calendar standalone)
  const [calExpenseItem, setCalExpenseItem] = useState<string>('');
  const [calExpenseAmount, setCalExpenseAmount] = useState<string>('');

  // Mileage & Expense data for display on calendar
  const [mileageEntries, setMileageEntries] = useState<MileageEntry[]>([]);
  const [expenseEntries, setExpenseEntries] = useState<ExpenseEntry[]>([]);

  // Get selected resource details
  const getSelectedResource = useCallback(() => {
    return resources.find(r => r.autoTaskId === selectedResourceId) || null;
  }, [resources, selectedResourceId]);

  // Helper to get filter parameters based on viewing selection
  const _getViewingFilters = useCallback((): { resourceId?: number; resourceName?: string } => {
    if (viewingSelection === 'all' || viewingSelection === '') {
      if (!isAdmin && viewingSelection === '' && selectedResourceId) {
        return { resourceId: selectedResourceId };
      }
      return {};
    } else if (viewingSelection.startsWith('contractor:')) {
      return { resourceName: viewingSelection.replace('contractor:', '') };
    } else {
      const id = parseInt(viewingSelection, 10);
      return isNaN(id) ? {} : { resourceId: id };
    }
  }, [viewingSelection, selectedResourceId, isAdmin]);
  void _getViewingFilters; // Keep for future use

  // Load resources
  useEffect(() => {
    async function loadResources() {
      try {
        const data = await getResources();
        const filtered = data.filter(r =>
          r.isActive &&
          r.licenseType !== 3 &&
          r.autoTaskId !== 4 &&
          !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
        );
        setResources(filtered);
      } catch (err) {
        console.error('Failed to load resources:', err);
      }
    }
    loadResources();
  }, []);

  // Load companies
  useEffect(() => {
    async function loadCompanies() {
      try {
        const data = await getActiveCompanies();
        const sorted = data.sort((a, b) => a.companyName.localeCompare(b.companyName));
        setCompanies(sorted);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    }
    loadCompanies();
  }, []);

  // Load tickets for selected company
  useEffect(() => {
    if (!selectedCompany) {
      setCompanyTickets([]);
      return;
    }
    const companyId = selectedCompany.autoTaskId;
    async function loadCompanyTickets() {
      setLoadingTickets(true);
      try {
        const tickets = await getTickets({ companyId, limit: 100 });
        setCompanyTickets(tickets);
      } catch (err) {
        console.error('Failed to load company tickets:', err);
        setCompanyTickets([]);
      } finally {
        setLoadingTickets(false);
      }
    }
    loadCompanyTickets();
  }, [selectedCompany]);

  // Load bid-jobs and TimeTech projects for selected company
  useEffect(() => {
    if (!selectedCompany) {
      setBidJobs([]);
      setSelectedBidJob(null);
      setTimetechProjects([]);
      return;
    }
    const companyId = selectedCompany.autoTaskId;
    async function loadBidJobsAndProjects() {
      setLoadingBidJobs(true);
      try {
        // Load bid-jobs and TimeTech projects in parallel
        const [bidJobList, projectList] = await Promise.all([
          getBidJobsForCompany(companyId),
          getTimeTechProjectsForCompany(companyId).catch(() => [] as TimeTechProject[]),
        ]);
        setBidJobs(bidJobList);
        setTimetechProjects(projectList);
        // Auto-select the default bid-job
        const defaultBidJob = getDefaultBidJob(bidJobList, companyId, selectedResourceId || undefined);
        setSelectedBidJob(defaultBidJob);
      } catch (err) {
        console.error('Failed to load bid-jobs:', err);
        setBidJobs([]);
        setSelectedBidJob(null);
        setTimetechProjects([]);
      } finally {
        setLoadingBidJobs(false);
      }
    }
    loadBidJobsAndProjects();
  }, [selectedCompany]);

  // Auto-update ticket queue when bid-job changes
  // PCP bid-jobs -> Proactive Checks queue, other bid-jobs -> Level I Support
  useEffect(() => {
    if (!selectedBidJob?.bidJobNumber) {
      return;
    }
    const isPCPBidJob = selectedBidJob.bidJobNumber.toUpperCase().startsWith('PCP');
    setNewTicketQueue(isPCPBidJob ? 'Proactive Checks' : 'Level I Support');
  }, [selectedBidJob]);

  // Load calendar data when month or viewing selection changes
  const loadCalendarData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      let resourceId: number | undefined;
      let resourceName: string | undefined;

      if (viewingSelection === 'all' || viewingSelection === '') {
        if (!isAdmin && viewingSelection === '') {
          resourceId = selectedResourceId || undefined;
        }
      } else if (viewingSelection.startsWith('contractor:')) {
        resourceName = viewingSelection.replace('contractor:', '');
      } else {
        resourceId = parseInt(viewingSelection, 10);
        if (!isNaN(resourceId)) {
          setSelectedResourceId(resourceId);
        }
      }

      const [data, mileage, expenses] = await Promise.all([
        getCalendarMonth(year, month, resourceId, resourceName),
        getMileageEntries(year, month, resourceId).catch(() => []),
        getExpenseEntries(year, month, resourceId).catch(() => []),
      ]);
      setMonthData(data);
      setMileageEntries(mileage);
      setExpenseEntries(expenses);

      // No need to initialize - all companies shown by default (hiddenCompanies is empty)
    } catch (err) {
      console.error('Failed to load calendar data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      setMonthData(null);
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewingSelection, selectedResourceId, isAdmin]);

  useEffect(() => { loadCalendarData(); }, [loadCalendarData]);

  // Load mileage rate setting
  useEffect(() => {
    getSystemSetting<string>('mileageRatePerMile')
      .then(rate => { if (rate) setCalMileageRate(parseFloat(String(rate)) || 0.725); })
      .catch(() => { /* use default */ });
  }, []);

  // Calculate max visible events based on available space
  useEffect(() => {
    const calculateMaxEvents = () => {
      if (!calendarGridRef.current) return;

      const gridHeight = calendarGridRef.current.clientHeight;
      // Calculate number of rows based on current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const startDayOfWeek = firstDay.getDay();
      const totalCells = startDayOfWeek + daysInMonth;
      const numRows = Math.ceil(totalCells / 7);

      if (numRows === 0 || gridHeight === 0) return;

      const rowHeight = gridHeight / numRows;
      const availableForEntries = rowHeight - DAY_HEADER_HEIGHT - DAY_FOOTER_HEIGHT;
      const maxEvents = Math.floor(availableForEntries / ENTRY_HEIGHT);

      // Clamp between min and max
      const clamped = Math.max(MIN_VISIBLE_EVENTS, Math.min(maxEvents, MAX_VISIBLE_EVENTS_CAP));
      setMaxVisibleEvents(clamped);
    };

    calculateMaxEvents();

    // Recalculate on window resize
    const resizeObserver = new ResizeObserver(calculateMaxEvents);
    if (calendarGridRef.current) {
      resizeObserver.observe(calendarGridRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [currentDate]);

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        // Collapse entire search when clicking outside
        setSearchExpanded(false);
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when expanded and pre-fill dates
  useEffect(() => {
    if (searchExpanded) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      // Pre-fill date range with current month if empty
      if (!searchFilters.dateFrom && !searchFilters.dateTo) {
        const { first, last } = getMonthDateRange(currentDate);
        setSearchFilters(f => ({ ...f, dateFrom: first, dateTo: last }));
      }
    }
  }, [searchExpanded, currentDate, searchFilters.dateFrom, searchFilters.dateTo]);

  // Auto-focus company search when add modal opens
  useEffect(() => {
    if (showAddModal && !selectedCompany && companySearchRef.current) {
      setTimeout(() => companySearchRef.current?.focus(), 100);
    }
  }, [showAddModal, selectedCompany]);

  // Scroll highlighted company into view
  useEffect(() => {
    if (highlightedCompanyIndex >= 0 && companyDropdownRef.current) {
      const items = companyDropdownRef.current.querySelectorAll('button');
      const item = items[highlightedCompanyIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedCompanyIndex]);

  // Companies with hours this month
  const companiesWithHours = (() => {
    if (!monthData?.entries) return [];
    const map = new Map<string, { name: string; hours: number; count: number }>();
    for (const e of monthData.entries) {
      const n = normalizeCompanyName(e.companyName);
      if (!n || n === 'Unknown') continue;
      const ex = map.get(n);
      if (ex) { ex.hours += e.hoursWorked; ex.count++; }
      else map.set(n, { name: n, hours: e.hoursWorked, count: 1 });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  // Toggle checkbox - add/remove from hidden set
  const toggleCompany = (name: string) => {
    setHiddenCompanies(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); // Was hidden, now show
      else next.add(name); // Was visible, now hide
      // Update explicit whitelist: visible companies for this month
      const visible = new Set<string>();
      for (const c of companiesWithHours) {
        if (!next.has(c.name)) visible.add(c.name);
      }
      setExplicitlySelected(visible.size > 0 ? visible : null);
      return next;
    });
  };

  // Click on client name - hide all others (show only that client)
  const selectOnlyClient = (name: string) => {
    const allOthers = new Set(companiesWithHours.map(c => c.name).filter(n => n !== name));
    setHiddenCompanies(allOthers);
    setExplicitlySelected(new Set([name]));
  };

  const selectAll = () => { setHiddenCompanies(new Set()); setExplicitlySelected(null); }; // Show all = clear explicit filter
  const selectNone = () => { setHiddenCompanies(new Set(companiesWithHours.map(c => c.name))); setExplicitlySelected(new Set()); }; // Hide all

  // When month changes and user has an explicit filter, hide any new companies not in the whitelist
  useEffect(() => {
    if (!explicitlySelected || !monthData?.entries) return;
    const newHidden = new Set(hiddenCompanies);
    let changed = false;
    for (const e of monthData.entries) {
      const n = normalizeCompanyName(e.companyName);
      if (n && n !== 'Unknown' && !explicitlySelected.has(n) && !newHidden.has(n)) {
        newHidden.add(n);
        changed = true;
      }
    }
    if (changed) setHiddenCompanies(newHidden);
  }, [monthData?.entries, explicitlySelected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute selectedCompanies for child components (all companies except hidden)
  // This gives children a "whitelist" format they expect
  const selectedCompanies = useMemo(() => {
    if (!monthData?.entries) return new Set<string>();
    const all = new Set<string>();
    for (const e of monthData.entries) {
      const n = normalizeCompanyName(e.companyName);
      if (n && n !== 'Unknown' && !hiddenCompanies.has(n)) {
        all.add(n);
      }
    }
    return all;
  }, [monthData?.entries, hiddenCompanies]);

  const goToToday = () => setCurrentDate(new Date());

  // View mode-aware navigation
  const prevPeriod = () => {
    const d = new Date(currentDate);
    switch (viewMode) {
      case 'day':
        d.setDate(d.getDate() - 1);
        break;
      case 'week5':
      case 'week7':
        d.setDate(d.getDate() - 7);
        break;
      case 'year':
        d.setFullYear(d.getFullYear() - 1);
        break;
      case 'schedule':
        d.setMonth(d.getMonth() - 1);
        break;
      case 'reconcile':
      case 'maintenance':
        return; // locked to current month
      case 'compare':
        d.setMonth(d.getMonth() - 1);
        break;
      default: // month
        d.setMonth(d.getMonth() - 1);
    }
    setCurrentDate(d);
  };

  const nextPeriod = () => {
    const d = new Date(currentDate);
    switch (viewMode) {
      case 'day':
        d.setDate(d.getDate() + 1);
        break;
      case 'week5':
      case 'week7':
        d.setDate(d.getDate() + 7);
        break;
      case 'year':
        d.setFullYear(d.getFullYear() + 1);
        break;
      case 'schedule':
        d.setMonth(d.getMonth() + 1);
        break;
      case 'reconcile':
      case 'maintenance':
        return; // locked to current month
      case 'compare':
        d.setMonth(d.getMonth() + 1);
        break;
      default: // month
        d.setMonth(d.getMonth() + 1);
    }
    setCurrentDate(d);
  };

  // Get display text for current period based on view mode
  const getPeriodDisplay = (): string => {
    switch (viewMode) {
      case 'day':
        return formatDisplayDate(currentDate);
      case 'week5': {
        const dates = getWorkWeekDates(currentDate);
        const start = dates[0];
        const end = dates[dates.length - 1];
        if (start.getMonth() === end.getMonth()) {
          return `${shortMonthNames[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
        }
        return `${shortMonthNames[start.getMonth()]} ${start.getDate()} - ${shortMonthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
      }
      case 'week7': {
        const dates = getWeekDates(currentDate);
        const start = dates[0];
        const end = dates[dates.length - 1];
        if (start.getMonth() === end.getMonth()) {
          return `${shortMonthNames[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
        }
        return `${shortMonthNames[start.getMonth()]} ${start.getDate()} - ${shortMonthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
      }
      case 'year':
        return `${currentDate.getFullYear()}`;
      case 'schedule':
        return `${shortMonthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'compare':
        return `Compare: ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'reconcile':
        return `Reconcile: ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'maintenance':
        return `Maintenance: ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      default:
        return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  // Save viewMode to localStorage
  useEffect(() => {
    localStorage.setItem('autotask-hub-calendar-view', viewMode);
  }, [viewMode]);

  // Keyboard shortcuts for view mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Don't trigger if any modal is open
      if (showAddModal || showNewTicketModal || showPendingModal) return;

      switch (e.key.toUpperCase()) {
        case 'D': setViewMode('day'); break;
        case 'W': setViewMode('week5'); break;
        case 'M': setViewMode('month'); break;
        case 'Y': setViewMode('year'); break;
        case 'A': setViewMode('schedule'); break;
        case 'C': setViewMode('compare'); break;
        case 'R': setViewMode('reconcile'); setCurrentDate(new Date()); break;
        case 'T': goToToday(); break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAddModal, showNewTicketModal, showPendingModal]);

  const miniCalendarPrevMonth = () => setMiniCalendarDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() - 1, 1));
  const miniCalendarNextMonth = () => setMiniCalendarDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() + 1, 1));

  // Search functions
  // Copy client hours summary to clipboard
  const [copyFeedback, setCopyFeedback] = useState(false);
  const handleCopyClientSummary = async () => {
    if (!companiesWithHours.length) return;
    const lines = companiesWithHours.map(c => `${c.name} - ${c.hours.toFixed(2)}hrs`);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for iframe/embed where clipboard API is blocked
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 1500);
  };

  const handleSearchToggle = () => {
    if (searchExpanded) {
      setSearchExpanded(false);
      setShowFilterDropdown(false);
    } else {
      setSearchExpanded(true);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applyFilters();
    else if (e.key === 'Escape') { setSearchExpanded(false); setShowFilterDropdown(false); }
  };

  const applyFilters = () => {
    setActiveFilters({ ...searchFilters });
    setShowFilterDropdown(false);
  };

  const clearFilters = () => {
    const { first, last } = getMonthDateRange(currentDate);
    setSearchFilters({ ...emptyFilters, dateFrom: first, dateTo: last });
    setActiveFilters(emptyFilters);
    setShowFilterDropdown(false);
  };

  const hasActiveFilters = activeFilters.query || activeFilters.dateFrom || activeFilters.dateTo ||
    activeFilters.minHours || activeFilters.maxHours;

  // Check if an entry matches search filters
  const matchesSearchFilters = (entry: CalendarTimeEntry): boolean => {
    if (!hasActiveFilters) return true;
    if (activeFilters.query) {
      const query = activeFilters.query.toLowerCase();
      const matchesQuery = entry.companyName.toLowerCase().includes(query) ||
        entry.summaryNotes.toLowerCase().includes(query);
      if (!matchesQuery) return false;
    }
    if (activeFilters.dateFrom && entry.dateWorked < activeFilters.dateFrom) return false;
    if (activeFilters.dateTo && entry.dateWorked > activeFilters.dateTo) return false;
    if (activeFilters.minHours && entry.hoursWorked < parseFloat(activeFilters.minHours)) return false;
    if (activeFilters.maxHours && entry.hoursWorked > parseFloat(activeFilters.maxHours)) return false;
    return true;
  };

  // Reset form
  const resetForm = () => {
    setCompanySearch('');
    setHighlightedCompanyIndex(-1);
    setSelectedCompany(null);
    setCompanyTickets([]);
    setSelectedTicket(null);
    setIsCreatingNewTicket(false);
    setNewTicketTitle('');
    setNewTicketQueue('Level I Support');
    setNewTicketPriority('Medium');
    setAutoCompleteTicket(true);
    setCloseExistingTicket(false);
    setBatchTickets([]);
    setBatchResults(null);
    setTicketSearchFilter('');
    setShowClosedTickets(false);
    setHoursWorked('');
    setSummaryNotes('');
    setFormError(null);
    setBidJobs([]);
    setSelectedBidJob(null);
    setEntryType('time');
    setCalMileageStart('Office');
    setCalMileageDestination('');
    setCalOdometerStart('');
    setCalOdometerEnd('');
    setCalMileageItem('');
    setCalEstimatedMiles(null);
    setCalExpenseItem('');
    setCalExpenseAmount('');
  };

  // Helper: toggle a ticket in/out of the batch list
  const toggleBatchTicket = (ticket: Ticket) => {
    setBatchTickets(prev => {
      const exists = prev.find(bt => bt.ticket.autoTaskId === ticket.autoTaskId);
      if (exists) {
        return prev.filter(bt => bt.ticket.autoTaskId !== ticket.autoTaskId);
      }
      return [...prev, { ticket, hoursWorked: '', closeTicket: false, summaryNotes: '' }];
    });
    // Clear single-ticket selection when entering batch mode
    setSelectedTicket(null);
    setIsCreatingNewTicket(false);
  };

  const updateBatchEntry = (ticketId: number, updates: Partial<Omit<BatchTicketEntry, 'ticket'>>) => {
    setBatchTickets(prev => prev.map(bt =>
      bt.ticket.autoTaskId === ticketId ? { ...bt, ...updates } : bt
    ));
  };

  // Submit new time entry
  const handleSubmitEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    const resource = getSelectedResource();
    if (!resource) { setFormError('Please select a technician first'); return; }
    if (!selectedCompany) { setFormError('Please select a company'); return; }
    if (!modalDate) { setFormError('Please select a date'); return; }

    // For non-custom clients, validate ticket selection
    const isCustomClient = selectedCompany.source === 'manual';
    const isBatchMode = batchTickets.length > 1;

    // Description required: per-entry in batch mode, shared field otherwise
    if (!isBatchMode && !summaryNotes) { setFormError('Please fill in a description'); return; }

    if (!isCustomClient && !isBatchMode) {
      if (!hoursWorked) { setFormError('Please fill in hours'); return; }
      if (isCreatingNewTicket) {
        if (!newTicketTitle.trim()) { setFormError('Please enter a ticket title'); return; }
      } else if (batchTickets.length === 1) {
        // Single ticket from checkbox list — use it
      } else if (!selectedTicket) {
        setFormError('Please select a ticket'); return;
      }
    }

    if (isBatchMode) {
      // Validate all batch entries have hours
      const missingHours = batchTickets.filter(bt => !bt.hoursWorked || parseFloat(bt.hoursWorked) <= 0);
      if (missingHours.length > 0) {
        setFormError(`Please enter hours for all tickets (${missingHours.map(bt => bt.ticket.ticketNumber).join(', ')})`);
        return;
      }
      // Validate all batch entries have descriptions
      const missingDesc = batchTickets.filter(bt => !bt.summaryNotes.trim());
      if (missingDesc.length > 0) {
        setFormError(`Please enter a description for all tickets (${missingDesc.map(bt => bt.ticket.ticketNumber).join(', ')})`);
        return;
      }
    }

    setFormSubmitting(true);
    setFormError(null);
    setBatchResults(null);

    try {
      const dateWorked = `${modalDate.getFullYear()}-${String(modalDate.getMonth() + 1).padStart(2, '0')}-${String(modalDate.getDate()).padStart(2, '0')}`;
      const companyId = selectedCompany.autoTaskId;
      const companyName = selectedCompany.companyName;

      // Handle custom clients separately - they don't have AutoTask tickets
      if (isCustomClient) {
        await addCustomClientTimeEntry({
          resourceId: resource.autoTaskId,
          resourceName: `${resource.firstName} ${resource.lastName}`,
          companyId,
          companyName,
          hoursWorked: parseFloat(hoursWorked),
          summaryNotes,
          dateWorked,
        });
        setShowAddModal(false);
        resetForm();
        await loadCalendarData();
        return;
      }

      // Determine TimeTech project based on bid-job type
      const bidJobProject = getProjectForBidJob(selectedBidJob, timetechProjects);

      // ─── BATCH MODE: multiple tickets ───
      if (isBatchMode) {
        const batchResult = await addBatchTimeEntries({
          resourceId: resource.autoTaskId,
          resourceName: `${resource.firstName} ${resource.lastName}`,
          dateWorked,
          summaryNotes: summaryNotes || batchTickets[0]?.summaryNotes || '',
          companyId,
          companyName,
          bidJobNumber: selectedBidJob?.bidJobNumber,
          bidJobDescription: selectedBidJob?.bidJobDescription,
          projectId: bidJobProject?.projectId,
          projectName: bidJobProject?.projectName,
          entries: batchTickets.map(bt => ({
            ticketId: bt.ticket.autoTaskId,
            ticketNumber: bt.ticket.ticketNumber,
            ticketTitle: bt.ticket.title,
            hoursWorked: parseFloat(bt.hoursWorked),
            closeTicket: bt.closeTicket,
            summaryNotes: bt.summaryNotes,
          })),
        });

        // Detect silent close failures: close was requested but didn't happen
        const closeFailures = batchResult.results.filter(
          r => r.success && r.closeRequested && !r.closedTicket
        );

        if (batchResult.failCount > 0) {
          // Partial failure — show results inline
          setBatchResults(batchResult);
          setFormError(`${batchResult.failCount} of ${batchResult.total} entries failed`);
          addToast('warning', `${batchResult.successCount} saved, ${batchResult.failCount} failed`);
        } else if (closeFailures.length > 0) {
          // Time entries saved but some tickets didn't close — surface it
          setBatchResults(batchResult);
          const tickets = closeFailures.map(r => r.ticketNumber).join(', ');
          setFormError(
            `Time entries saved, but ${closeFailures.length} ticket(s) didn't close: ${tickets}. ` +
            `Check AutoTask for business-rule issues, then close manually.`
          );
          addToast('warning', `Time saved — ${closeFailures.length} ticket(s) didn't close`);
        } else {
          addToast('success', `${batchResult.successCount} time entries saved`);
          setShowAddModal(false);
          resetForm();
        }
        await loadCalendarData();
        return;
      }

      // ─── SINGLE MODE (1 ticket selected from checkboxes or dropdown) ───
      const singleBatch = batchTickets.length === 1 ? batchTickets[0] : null;
      const effectiveTicket = singleBatch?.ticket || selectedTicket;
      // For single-ticket from checkbox list, use the standalone closeExistingTicket state
      // (the batch entry's closeTicket is only used in multi-ticket mode)
      const effectiveCloseTicket = singleBatch ? closeExistingTicket : closeExistingTicket;

      // Regular AutoTask client flow
      const dueDateTime = `${dateWorked}T17:00:00`;
      let ticketId: number;
      let ticketNumber: string;

      if (isCreatingNewTicket) {
        const priorityMap: Record<string, number> = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };

        const PROACTIVE_CHECKS_QUEUE_ID = 29683487;
        const isPCPBidJob = selectedBidJob?.bidJobNumber?.toUpperCase().startsWith('PCP');
        const queueID = isPCPBidJob ? PROACTIVE_CHECKS_QUEUE_ID : undefined;

        const newTicket = await createTicketInAutoTask({
          companyID: companyId,
          title: newTicketTitle.trim(),
          description: summaryNotes,
          dueDateTime,
          priority: priorityMap[newTicketPriority] || 3,
          assignedResourceID: resource.autoTaskId,
          assignedResourceRoleID: resource.defaultRoleId,
          status: autoCompleteTicket ? 5 : 1,
          queueID,
        });
        ticketId = newTicket.id;
        ticketNumber = newTicket.ticketNumber;
      } else {
        ticketId = effectiveTicket!.autoTaskId;
        ticketNumber = effectiveTicket!.ticketNumber;
      }

      const ticketTitle = isCreatingNewTicket ? newTicketTitle.trim() : effectiveTicket?.title;

      const timeEntryResult = await addTimeEntry(ticketId, {
        resourceID: resource.autoTaskId,
        roleID: resource.defaultRoleId,
        hoursWorked: parseFloat(hoursWorked),
        summaryNotes,
        dateWorked,
        companyId,
        companyName,
        ticketNumber,
        ticketTitle,
        bidJobNumber: selectedBidJob?.bidJobNumber,
        bidJobDescription: selectedBidJob?.bidJobDescription,
        projectId: bidJobProject?.projectId,
        projectName: bidJobProject?.projectName,
      });

      // Close existing ticket if checkbox was checked
      if (effectiveCloseTicket && !isCreatingNewTicket && effectiveTicket) {
        try {
          if (!effectiveTicket.assignedResourceId) {
            await updateTicket(effectiveTicket.autoTaskId, {
              assignedResourceID: resource.autoTaskId,
              assignedResourceRoleID: resource.defaultRoleId,
            });
          }
          await addTicketNote(effectiveTicket.autoTaskId, {
            description: summaryNotes || 'Completed',
            noteType: 2,
            publish: 1,
            creatorResourceID: resource.autoTaskId,
          });
          await updateTicket(effectiveTicket.autoTaskId, { status: 5 });
        } catch (closeErr) {
          console.error('Failed to close ticket:', closeErr);
        }
      }

      const timetechFailed = timeEntryResult.timetech && !timeEntryResult.timetech.success;
      if (timetechFailed) {
        addToast('warning', `Time saved to AutoTask but TimeTech sync failed: ${timeEntryResult.timetech?.error}`);
      }
      setShowAddModal(false);
      resetForm();
      await loadCalendarData();
    } catch (err) {
      console.error('Failed to create time entry:', err);
      setFormError(err instanceof Error ? err.message : 'Failed to create time entry');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Submit mileage entry from calendar
  const handleSubmitMileage = async (e: React.FormEvent) => {
    e.preventDefault();
    const resource = getSelectedResource();
    if (!resource) { setFormError('Please select a technician first'); return; }
    if (!modalDate) { setFormError('No date selected'); return; }

    const oStart = parseFloat(calOdometerStart);
    const oEnd = parseFloat(calOdometerEnd);
    if (isNaN(oStart) || isNaN(oEnd) || oEnd <= oStart) {
      setFormError('Odometer End must be greater than Start');
      return;
    }

    setFormSubmitting(true);
    setFormError(null);

    try {
      const dateWorked = `${modalDate.getFullYear()}-${String(modalDate.getMonth() + 1).padStart(2, '0')}-${String(modalDate.getDate()).padStart(2, '0')}`;

      await createMileageEntry({
        resourceId: resource.autoTaskId,
        resourceName: `${resource.firstName} ${resource.lastName}`,
        dateWorked,
        startLocation: calMileageStart,
        startCompanyId: calMileageStart === 'Office' ? undefined : selectedCompany?.autoTaskId,
        destination: calMileageDestination || selectedCompany?.companyName || '',
        destinationCompanyId: selectedCompany?.autoTaskId,
        estimatedMiles: calEstimatedMiles || undefined,
        odometerStart: oStart,
        odometerEnd: oEnd,
        ratePerMile: calMileageRate,
        item: calMileageItem || undefined,
        ticketId: selectedTicket?.autoTaskId,
        ticketNumber: selectedTicket?.ticketNumber,
      });

      setShowAddModal(false);
      resetForm();
      await loadCalendarData();
    } catch (err) {
      console.error('Failed to create mileage entry:', err);
      setFormError(err instanceof Error ? err.message : 'Failed to create mileage entry');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Submit expense entry from calendar
  const handleSubmitExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    const resource = getSelectedResource();
    if (!resource) { setFormError('Please select a technician first'); return; }
    if (!modalDate) { setFormError('No date selected'); return; }
    if (!calExpenseItem.trim()) { setFormError('Please enter an item description'); return; }
    const amount = parseFloat(calExpenseAmount);
    if (isNaN(amount) || amount <= 0) { setFormError('Please enter a valid amount'); return; }

    setFormSubmitting(true);
    setFormError(null);

    try {
      const dateWorked = `${modalDate.getFullYear()}-${String(modalDate.getMonth() + 1).padStart(2, '0')}-${String(modalDate.getDate()).padStart(2, '0')}`;

      await createExpenseEntry({
        resourceId: resource.autoTaskId,
        resourceName: `${resource.firstName} ${resource.lastName}`,
        dateWorked,
        item: calExpenseItem.trim(),
        amount,
        companyId: selectedCompany?.autoTaskId,
        companyName: selectedCompany?.companyName,
        ticketId: selectedTicket?.autoTaskId,
        ticketNumber: selectedTicket?.ticketNumber,
      });

      setShowAddModal(false);
      resetForm();
      await loadCalendarData();
    } catch (err) {
      console.error('Failed to create expense entry:', err);
      setFormError(err instanceof Error ? err.message : 'Failed to create expense entry');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Dispatch form submit based on entry type
  const handleFormSubmit = (e: React.FormEvent) => {
    if (entryType === 'mileage') return handleSubmitMileage(e);
    if (entryType === 'expense') return handleSubmitExpense(e);
    return handleSubmitEntry(e);
  };

  // Helper to get mileage entries for a specific day
  const getMileageForDay = (day: number): MileageEntry[] => {
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return mileageEntries.filter(e => e.dateWorked === key);
  };

  // Helper to get expense entries for a specific day
  const getExpensesForDay = (day: number): ExpenseEntry[] => {
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return expenseEntries.filter(e => e.dateWorked === key);
  };

  // Fetch mileage estimate when start/destination changes
  useEffect(() => {
    if (!showAddModal || entryType !== 'mileage' || !selectedCompany) {
      setCalEstimatedMiles(null);
      return;
    }

    const from: 'office' | number = calMileageStart === 'Office' ? 'office' : selectedCompany.autoTaskId;
    const toCompanyId = selectedCompany.autoTaskId;

    getMileageEstimate(from, toCompanyId)
      .then(result => setCalEstimatedMiles(result.estimatedMiles > 0 ? result.estimatedMiles : null))
      .catch(() => setCalEstimatedMiles(null));
  }, [showAddModal, entryType, calMileageStart, selectedCompany]);

  // Get mini calendar days (6 rows)
  const getMiniCalendarDays = () => {
    const year = miniCalendarDate.getFullYear();
    const month = miniCalendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: { day: number; currentMonth: boolean }[] = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, currentMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, currentMonth: true });
    }
    let nextDay = 1;
    while (days.length < 42) {
      days.push({ day: nextDay++, currentMonth: false });
    }
    return days;
  };

  // Get days in main calendar month
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay();
    const days: (number | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    while (days.length % 7 !== 0) days.push(null);
    return days;
  };

  // Get filtered entries for a day
  const getEntriesForDay = (day: number): CalendarTimeEntry[] => {
    if (!monthData?.byDate) return [];
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const entries = monthData.byDate[key] || [];
    // Show all entries except those from hidden companies
    return entries
      .filter(e => !hiddenCompanies.has(normalizeCompanyName(e.companyName)))
      .filter(matchesSearchFilters);
  };

  const days = getDaysInMonth();
  const miniDays = getMiniCalendarDays();
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  const isMiniCurrentMonth = miniCalendarDate.getMonth() === today.getMonth() && miniCalendarDate.getFullYear() === today.getFullYear();

  const filteredStats = {
    totalHours: monthData?.entries?.filter(e => !hiddenCompanies.has(normalizeCompanyName(e.companyName)) && matchesSearchFilters(e)).reduce((s, e) => s + e.hoursWorked, 0) || 0,
    totalEntries: monthData?.entries?.filter(e => !hiddenCompanies.has(normalizeCompanyName(e.companyName)) && matchesSearchFilters(e)).length || 0,
  };

  const filteredCompanies = companies.filter(c => c.companyName.toLowerCase().includes(companySearch.toLowerCase())).slice(0, 10);

  const openEntryPopup = (entry: CalendarTimeEntry, event: React.MouseEvent) => {
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = overflowPopup ? overflowPopup.x + 400 : rect.right + 8;
    const y = rect.top;
    setPopupPosition({ x: Math.min(x, window.innerWidth - 320), y: Math.min(Math.max(y, 10), window.innerHeight - 280) });
    setViewingEntry(entry);
  };

  const openOverflowPopup = (day: number, entries: CalendarTimeEntry[], event: React.MouseEvent) => {
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setOverflowPopup({ day, entries, x: Math.min(rect.right + 8, window.innerWidth - 400), y: Math.min(rect.top, window.innerHeight - 350) });
    setViewingEntry(null);
  };

  const openAddModal = (day: number) => {
    if (!selectedResourceId) return;
    setModalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    resetForm();
    // Pre-select company only if user has actively filtered to exactly one
    if (hiddenCompanies.size > 0) {
      const visibleCompanies = companiesWithHours.filter(c => !hiddenCompanies.has(c.name));
      if (visibleCompanies.length === 1) {
        const matchingCompany = companies.find(c =>
          normalizeCompanyName(c.companyName) === visibleCompanies[0].name
        );
        if (matchingCompany) {
          setSelectedCompany(matchingCompany);
        }
      }
    }
    setShowAddModal(true);
  };

  const handleMiniCalendarDayClick = (day: number, currentMonth: boolean) => {
    if (!selectedResourceId) return;
    if (currentMonth) {
      setCurrentDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth(), day));
    }
  };

  const closeAllPopups = () => {
    setViewingEntry(null);
    setOverflowPopup(null);
  };

  // Handler for adding entry from view components
  const handleAddEntry = (date: Date) => {
    if (!selectedResourceId) return;
    setModalDate(date);
    resetForm();
    // Pre-select company only if user has actively filtered to exactly one
    if (hiddenCompanies.size > 0) {
      const visibleCompanies = companiesWithHours.filter(c => !hiddenCompanies.has(c.name));
      if (visibleCompanies.length === 1) {
        const matchingCompany = companies.find(c =>
          normalizeCompanyName(c.companyName) === visibleCompanies[0].name
        );
        if (matchingCompany) {
          setSelectedCompany(matchingCompany);
        }
      }
    }
    setShowAddModal(true);
  };

  // Open add-entry modal with company pre-selected (used by CompareView)
  const handleAddEntryWithCompany = (date: Date, company: CompanyOption | null, prefill?: { hoursWorked: number }) => {
    if (!selectedResourceId) return;
    setModalDate(date);
    resetForm();
    if (company) {
      setSelectedCompany(company);
    }
    if (prefill?.hoursWorked) {
      setHoursWorked(String(prefill.hoursWorked));
    }
    setShowAddModal(true);
  };

  // Open edit modal for an entry
  const openEditModal = async (entry: CalendarTimeEntry) => {
    setEditingEntry(entry);
    setEditHours(String(entry.hoursWorked));
    setEditDate(entry.dateWorked);
    setEditSummary(entry.summaryNotes || '');
    setEditError(null);
    setDeleteConfirm(false);
    setViewingEntry(null);

    // Load bid-jobs for the entry's company
    if (entry.companyId) {
      try {
        const bidJobList = await getBidJobsForCompany(entry.companyId);
        setEditBidJobs(bidJobList);
        // Find current bid-job if set
        const currentBidJob = bidJobList.find(bj =>
          bj.bidJobNumber === entry.bidJobNumber && bj.bidJobDescription === entry.bidJobDescription
        );
        setEditSelectedBidJob(currentBidJob || null);
      } catch {
        setEditBidJobs([]);
        setEditSelectedBidJob(null);
      }
    }
  };

  // Save edited entry
  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    const hours = parseFloat(editHours);
    if (isNaN(hours) || hours <= 0) {
      setEditError('Please enter valid hours');
      return;
    }

    if (!editDate || !/^\d{4}-\d{2}-\d{2}$/.test(editDate)) {
      setEditError('Please enter a valid date');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const result = await updateCalendarTimeEntry(editingEntry.id, {
        hoursWorked: hours,
        dateWorked: editDate,
        summaryNotes: editSummary,
        bidJobNumber: editSelectedBidJob?.bidJobNumber,
        bidJobDescription: editSelectedBidJob?.bidJobDescription,
      });

      // Refresh calendar data
      await loadCalendarData();
      setEditingEntry(null);

      if (result.timetechWarning) {
        setTimeout(() => alert(`Entry saved, but TimeTech sync failed: ${result.timetechWarning}`), 50);
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update entry');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete entry (can be called from view popup or edit modal)
  const handleDeleteEntry = async (entryToDelete?: CalendarTimeEntry) => {
    const entry = entryToDelete || editingEntry;
    if (!entry) return;

    setEditLoading(true);
    setEditError(null);

    try {
      await deleteCalendarTimeEntry(entry.id);

      // Refresh calendar data
      await loadCalendarData();
      setEditingEntry(null);
      setViewingEntry(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setEditLoading(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (entry: CalendarTimeEntry, e: React.DragEvent) => {
    setDragEntry(entry);
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', entry.id);
    // Slight delay so the drag image renders before opacity change
    requestAnimationFrame(() => setDragEntry(entry));
  };

  const handleDragOver = (day: number, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDay(day);
  };

  const handleDragLeave = () => {
    setDropTargetDay(null);
  };

  const handleDrop = (day: number, e: React.DragEvent) => {
    e.preventDefault();
    setDropTargetDay(null);
    if (!dragEntry) return;

    // Build target date string
    const targetDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Don't show modal if dropping on the same day
    if (targetDate === dragEntry.dateWorked) {
      setDragEntry(null);
      return;
    }

    setDragActionModal({ entry: dragEntry, targetDate });
    setDragEntry(null);
    setDragActionError(null);
  };

  const handleDragEnd = () => {
    setDragEntry(null);
    setDropTargetDay(null);
    setDropTargetDate(null);
    // Close the overflow popup once the drag finishes (closing it during
    // dragstart would remove the source node and cancel the drag).
    setOverflowPopup(null);
  };

  // Date-string-based drop handlers for views whose cells can straddle a
  // month boundary (Week / Week-7). The month grid uses the day-number handlers above.
  const handleDragOverDate = (dateStr: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDate(dateStr);
  };

  const handleDragLeaveDate = () => {
    setDropTargetDate(null);
  };

  const handleDropOnDate = (dateStr: string, e: React.DragEvent) => {
    e.preventDefault();
    setDropTargetDate(null);
    if (!dragEntry) return;
    if (dateStr === dragEntry.dateWorked) {
      setDragEntry(null);
      return;
    }
    setDragActionModal({ entry: dragEntry, targetDate: dateStr });
    setDragEntry(null);
    setDragActionError(null);
  };

  // Move entry to a new date
  const handleDragMove = async () => {
    if (!dragActionModal) return;
    const { entry, targetDate } = dragActionModal;

    setDragActionLoading(true);
    setDragActionError(null);
    try {
      const result = await updateCalendarTimeEntry(entry.id, { dateWorked: targetDate });
      await loadCalendarData();
      setDragActionModal(null);

      if (result.timetechWarning) {
        // Defer alert so React can repaint (remove overlay) before blocking
        setTimeout(() => alert(`Entry moved, but TimeTech sync failed: ${result.timetechWarning}`), 50);
      }
    } catch (err) {
      setDragActionError(err instanceof Error ? err.message : 'Failed to move entry');
    } finally {
      setDragActionLoading(false);
    }
  };

  // Copy entry to a new date
  const handleDragCopy = async () => {
    if (!dragActionModal) return;
    const { entry, targetDate } = dragActionModal;

    setDragActionLoading(true);
    setDragActionError(null);
    try {
      const isCustomClient = entry.ticketId === 0;
      if (isCustomClient) {
        await addCustomClientTimeEntry({
          resourceId: entry.resourceId,
          resourceName: entry.resourceName,
          companyId: entry.companyId,
          companyName: entry.companyName,
          hoursWorked: entry.hoursWorked,
          summaryNotes: entry.summaryNotes,
          dateWorked: targetDate,
        });
      } else {
        await addTimeEntry(entry.ticketId, {
          resourceID: entry.resourceId,
          roleID: entry.roleId,
          hoursWorked: entry.hoursWorked,
          summaryNotes: entry.summaryNotes,
          dateWorked: targetDate,
          companyId: entry.companyId,
          companyName: entry.companyName,
          ticketNumber: entry.ticketNumber,
          ticketTitle: entry.ticketTitle,
          bidJobNumber: entry.bidJobNumber,
          bidJobDescription: entry.bidJobDescription,
        });
      }
      await loadCalendarData();
      setDragActionModal(null);
    } catch (err) {
      setDragActionError(err instanceof Error ? err.message : 'Failed to copy entry');
    } finally {
      setDragActionLoading(false);
    }
  };

  // Render the appropriate calendar view based on viewMode
  const renderMainView = () => {
    switch (viewMode) {
      case 'day':
        return (
          <DayView
            currentDate={currentDate}
            monthData={monthData}
            selectedCompanies={selectedCompanies}
            searchFilter={matchesSearchFilters}
            onEntryClick={openEntryPopup}
            onAddEntry={handleAddEntry}
          />
        );
      case 'week5':
      case 'week7':
        return (
          <WeekView
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            monthData={monthData}
            selectedCompanies={selectedCompanies}
            searchFilter={matchesSearchFilters}
            onEntryClick={openEntryPopup}
            onAddEntry={handleAddEntry}
            viewMode={viewMode}
            setViewMode={setViewMode}
            dragEntryId={dragEntry?.id ?? null}
            dropTargetDate={dropTargetDate}
            onEntryDragStart={handleDragStart}
            onEntryDragEnd={handleDragEnd}
            onDayDragOver={handleDragOverDate}
            onDayDragLeave={handleDragLeaveDate}
            onDayDrop={handleDropOnDate}
          />
        );
      case 'year':
        return (
          <YearView
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            selectedResourceId={selectedResourceId}
            selectedCompanies={selectedCompanies}
            searchFilter={matchesSearchFilters}
            setViewMode={setViewMode}
            onEntryClick={openEntryPopup}
            onAddEntry={handleAddEntry}
          />
        );
      case 'schedule':
        return (
          <ScheduleView
            currentDate={currentDate}
            selectedResourceId={selectedResourceId}
            monthData={monthData}
            selectedCompanies={selectedCompanies}
            searchFilter={matchesSearchFilters}
            onEntryClick={openEntryPopup}
            onAddEntry={handleAddEntry}
          />
        );
      case 'compare':
        return (
          <CompareView
            currentDate={currentDate}
            selectedResourceId={selectedResourceId}
            resources={resources}
            companies={companies}
            onAddEntry={handleAddEntryWithCompany}
            onEditEntry={openEditModal}
          />
        );
      case 'reconcile':
        return (
          <ReconcileView
            currentDate={currentDate}
            selectedResourceId={selectedResourceId}
            resources={resources}
            companies={companies}
            onAddEntry={handleAddEntryWithCompany}
            onEditEntry={openEditModal}
          />
        );
      case 'maintenance':
        return (
          <MaintenanceView
            currentDate={currentDate}
            resources={resources}
            companies={companies}
            userEmail={user?.email || ''}
          />
        );
      default:
        return null; // Month view is rendered inline below
    }
  };

  // Auto-select logged-in user's resource (non-admins always forced to own resource)
  useEffect(() => {
    if (!isAdmin && user?.email && resources.length > 0) {
      // Non-admins: ALWAYS force to their own resource (prevents stale localStorage leak)
      const matchedResource = resources.find(r =>
        r.email?.toLowerCase() === user.email.toLowerCase()
      );
      if (matchedResource) {
        const matchedId = String(matchedResource.autoTaskId);
        if (viewingSelection !== matchedId) {
          setViewingSelection(matchedId);
          setSelectedResourceId(matchedResource.autoTaskId);
          localStorage.setItem('autotask-hub-resource-id', matchedId);
          localStorage.setItem('autotask-hub-resource-email', matchedResource.email || '');
        }
      } else if (user.email === 'embed@hud.local') {
        // Embed mode fallback: use stored resource email
        const storedEmail = localStorage.getItem('autotask-hub-resource-email');
        if (storedEmail) {
          const fallbackMatch = resources.find(r =>
            r.email?.toLowerCase() === storedEmail.toLowerCase()
          );
          if (fallbackMatch) {
            const fallbackId = String(fallbackMatch.autoTaskId);
            if (viewingSelection !== fallbackId) {
              setViewingSelection(fallbackId);
              setSelectedResourceId(fallbackMatch.autoTaskId);
              localStorage.setItem('autotask-hub-resource-id', fallbackId);
            }
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.email, resources]);

  // Reset company selection when admin changes tech
  const handleTechChange = (value: string) => {
    setViewingSelection(value);
    if (value && !value.startsWith('contractor:') && value !== 'all') {
      const id = parseInt(value, 10);
      if (!isNaN(id)) {
        setSelectedResourceId(id);
        // Write to calendar-specific key — NOT the user identity key
        localStorage.setItem('autotask-hub-calendar-viewing', value);
      }
    } else if (value === 'all') {
      setSelectedResourceId(null);
      localStorage.setItem('autotask-hub-calendar-viewing', 'all');
    }
    // Reset to show all companies when changing tech
    setHiddenCompanies(new Set());
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Shared backdrop for popups */}
      {(viewingEntry || overflowPopup) && (
        <div className="fixed inset-0 z-40" onClick={closeAllPopups} />
      )}

      {/* Header Area - Google Calendar Style */}
      <PageHeader
        leftContent={
          searchExpanded ? (
            /* Search mode: back arrow + "Search" + search bar */
            <div ref={searchContainerRef} className="flex items-center gap-4 flex-1">
              <button
                onClick={handleSearchToggle}
                className="flex items-center gap-2 text-white hover:text-blue-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-lg font-semibold">Search</span>
              </button>

              {/* Search bar */}
              <div className="flex-1 max-w-2xl relative">
                <div className="flex items-center bg-slate-700 rounded-lg border border-slate-600 shadow-lg">
                  <div className="p-2 text-slate-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchFilters.query}
                    onChange={e => setSearchFilters(f => ({ ...f, query: e.target.value }))}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search"
                    className="flex-1 bg-transparent text-white text-sm py-2 focus:outline-none placeholder-slate-500"
                  />
                  {searchFilters.query && (
                    <button onClick={() => setSearchFilters(f => ({ ...f, query: '' }))} className="p-2 text-slate-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className={`p-2 border-l border-slate-600 transition-colors ${showFilterDropdown ? 'text-white' : 'text-slate-400 hover:text-white'}`}
                    title="Advanced search"
                  >
                    <svg className={`w-4 h-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Filter dropdown */}
                {showFilterDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-slate-800 rounded-lg border border-slate-600 shadow-2xl z-50">
                    <div className="p-4 space-y-4">
                      <div className="flex items-center gap-4">
                        <label className="text-sm text-slate-400 w-24">Keywords</label>
                        <input
                          type="text"
                          value={searchFilters.query}
                          onChange={e => setSearchFilters(f => ({ ...f, query: e.target.value }))}
                          placeholder="Keywords contained in entry"
                          className="flex-1 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="text-sm text-slate-400 w-24">Date</label>
                        <input
                          type="date"
                          value={searchFilters.dateFrom}
                          onChange={e => setSearchFilters(f => ({ ...f, dateFrom: e.target.value }))}
                          className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                        <span className="text-slate-500">to</span>
                        <input
                          type="date"
                          value={searchFilters.dateTo}
                          onChange={e => setSearchFilters(f => ({ ...f, dateTo: e.target.value }))}
                          className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="text-sm text-slate-400 w-24">Hours</label>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          value={searchFilters.minHours}
                          onChange={e => setSearchFilters(f => ({ ...f, minHours: e.target.value }))}
                          placeholder="Min"
                          className="w-24 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                        <span className="text-slate-500">to</span>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          value={searchFilters.maxHours}
                          onChange={e => setSearchFilters(f => ({ ...f, maxHours: e.target.value }))}
                          placeholder="Max"
                          className="w-24 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-4 p-3 border-t border-slate-700">
                      <button onClick={clearFilters} className="text-sm text-slate-400 hover:text-white transition-colors">Reset</button>
                      <button onClick={applyFilters} className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">Search</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Normal mode: Tech selector (admin only) + Today + nav + month + pending/failed */
            <div className="flex items-center gap-3">
              {/* Tech Selector - Only visible for admins */}
              {isAdmin && (
                <>
                  <select
                    value={viewingSelection}
                    onChange={(e) => handleTechChange(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-2 py-1 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="all">ALL (Everyone)</option>
                    <optgroup label="Employees">
                      {resources
                        .filter(r => !hiddenResourceIds.includes(r.autoTaskId))
                        .map(r => (
                          <option key={r.autoTaskId} value={r.autoTaskId}>{r.firstName} {r.lastName}</option>
                        ))}
                    </optgroup>
                    <optgroup label="Contractors">
                      {contractors.map(c => (
                        <option key={c.email} value={`contractor:${c.name}`}>{c.name}</option>
                      ))}
                    </optgroup>
                  </select>

                  {/* Separator */}
                  <div className="h-6 w-px bg-slate-600" />
                </>
              )}

              {/* Today Button */}
              <button onClick={goToToday} className="px-3 py-1 text-sm border border-slate-600 rounded-lg hover:bg-slate-700 text-slate-300 transition-colors">
                Today
              </button>

              {/* Period Navigation (hidden in reconcile — locked to current month) */}
              {viewMode !== 'reconcile' && (
              <div className="flex items-center">
                <button onClick={prevPeriod} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button onClick={nextPeriod} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              )}
              {/* Day bubble for day view */}
              {viewMode === 'day' && (
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  {currentDate.getDate()}
                </div>
              )}
              <span className="text-white font-medium">{getPeriodDisplay()}</span>

              {/* Refresh Button - right of month name */}
              <button onClick={() => loadCalendarData()} disabled={loading} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-50" title="Refresh">
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>

              {/* Pending/Failed Indicators */}
              {(monthData?.entries?.filter(e => e.syncStatus === 'pending').length || 0) > 0 && (
                <button onClick={() => { setPendingModalType('pending'); setShowPendingModal(true); }} className="flex items-center gap-1.5 bg-yellow-900/30 border border-yellow-600/50 rounded px-2 py-1 hover:bg-yellow-800/40 transition-colors cursor-pointer ml-2" title="Click to view pending entries">
                  <span className="text-xs text-yellow-400">Pending:</span>
                  <span className="text-sm font-medium text-yellow-400">{monthData?.entries?.filter(e => e.syncStatus === 'pending').length}</span>
                </button>
              )}
              {(monthData?.entries?.filter(e => e.syncStatus === 'failed').length || 0) > 0 && (
                <button onClick={() => { setPendingModalType('failed'); setShowPendingModal(true); }} className="flex items-center gap-1.5 bg-red-900/30 border border-red-600/50 rounded px-2 py-1 hover:bg-red-800/40 transition-colors cursor-pointer" title="Click to view failed entries">
                  <span className="text-xs text-red-400">Failed:</span>
                  <span className="text-sm font-medium text-red-400">{monthData?.entries?.filter(e => e.syncStatus === 'failed').length}</span>
                </button>
              )}
            </div>
          )
        }
        searchButton={
          !searchExpanded && (
            <div className="flex items-center gap-3">
              <ViewSelector viewMode={viewMode} onChange={(mode) => { setViewMode(mode); if (mode === 'reconcile' || mode === 'maintenance') setCurrentDate(new Date()); }} hiddenOptions={user?.email?.toLowerCase() === 'jgoode@umetech.net' ? [] : ['maintenance']} />
              <div className="relative">
                <button
                  onClick={handleSearchToggle}
                  className={`p-2 rounded-lg transition-colors ${hasActiveFilters ? 'text-blue-400 hover:bg-slate-700' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                  title="Search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
                {hasActiveFilters && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                )}
              </div>
              <button
                onClick={handleCopyClientSummary}
                className={`p-2 rounded-lg transition-colors ${copyFeedback ? 'text-green-400' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                title="Copy client hours summary"
              >
                {copyFeedback ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
          )
        }
        userEmail={user?.email}
      />

      {/* Error Display */}
      {error && (
        <div className="mx-3 mt-3 bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-red-400 text-sm">{error}</div>
      )}

      {/* Main Calendar Layout */}
      <div className="flex-1 flex gap-3 min-h-0 p-3">
        {/* LEFT SIDEBAR */}
        <div className="w-56 flex-shrink-0 flex flex-col bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {/* Mini Calendar */}
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">{monthNames[miniCalendarDate.getMonth()]} {miniCalendarDate.getFullYear()}</span>
              <div className="flex items-center">
                <button onClick={miniCalendarPrevMonth} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button onClick={miniCalendarNextMonth} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-0 text-center">
              {shortDays.map((d, i) => (
                <div key={i} className="text-[10px] text-slate-500 py-1">{d}</div>
              ))}
              {miniDays.map((item, idx) => {
                const isToday = isMiniCurrentMonth && item.currentMonth && item.day === today.getDate();
                return (
                  <button key={idx} onClick={() => handleMiniCalendarDayClick(item.day, item.currentMonth)} disabled={!selectedResourceId}
                    className={`text-xs p-0.5 rounded-full w-6 h-6 flex items-center justify-center mx-auto transition-colors disabled:cursor-not-allowed
                      ${item.currentMonth ? 'hover:bg-slate-700' : 'hover:bg-slate-700/50'}
                      ${isToday ? 'bg-blue-600 text-white font-medium' : item.currentMonth ? 'text-slate-300' : 'text-slate-600'}
                    `}
                  >{item.day}</button>
                );
              })}
            </div>
          </div>

          {/* Company list */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-slate-400">Clients:</span>
                <div className="flex gap-2 text-xs">
                  <button onClick={selectAll} className="text-blue-400 hover:text-blue-300">All</button>
                  <span className="text-slate-600">|</span>
                  <button onClick={selectNone} className="text-blue-400 hover:text-blue-300">None</button>
                </div>
              </div>
              {companiesWithHours.length === 0 ? (
                <div className="text-xs text-slate-500 py-2">No entries this month</div>
              ) : (
                <div className="space-y-1">
                  {companiesWithHours.map(c => {
                    const color = getCompanyColor(c.name);
                    const isChecked = !hiddenCompanies.has(c.name); // Visible if not hidden
                    return (
                      <div key={c.name} className="flex items-center gap-2 w-full py-1 px-1 rounded hover:bg-slate-700/50 transition-colors">
                        {/* Checkbox - toggles selection */}
                        <button onClick={() => toggleCompany(c.name)} className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-colors ${isChecked ? color.bg : 'border-2 border-slate-500 hover:border-slate-400'}`}>
                          {isChecked && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                        </button>
                        {/* Name - selects only this client */}
                        <button onClick={() => selectOnlyClient(c.name)} className={`text-xs flex-1 truncate text-left ${isChecked ? 'text-slate-200' : 'text-slate-500'} hover:text-white`}>{c.name}</button>
                        <span className={`text-xs ${isChecked ? 'text-slate-400' : 'text-slate-600'}`}>{formatHours(c.hours)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-700 bg-slate-800/50">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Total</span>
              <span className="text-white font-medium">{formatHours(filteredStats.totalHours)}</span>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-slate-400">Entries</span>
              <span className="text-slate-300">{filteredStats.totalEntries}</span>
            </div>
          </div>
        </div>

        {/* MAIN CALENDAR */}
        <div className="flex-1 flex flex-col min-w-0 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
          {viewMode === 'month' ? (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-slate-700">
                {dayNames.map(d => (
                  <div key={d} className="py-2 text-center text-xs font-medium text-slate-400 border-r border-slate-700/50 last:border-r-0">{d}</div>
                ))}
              </div>

              {/* Days grid - equal rows, no scroll */}
              <div ref={calendarGridRef} className="flex-1 grid grid-cols-7 overflow-hidden" style={{ gridAutoRows: '1fr' }}>
                {days.map((day, idx) => {
                  const isToday = isCurrentMonth && day === today.getDate();
                  const dayEntries = day ? getEntriesForDay(day) : [];
                  const dayHours = dayEntries.reduce((s, e) => s + e.hoursWorked, 0);
                  const visible = dayEntries.slice(0, maxVisibleEvents);
                  const overflow = dayEntries.length - maxVisibleEvents;
                  const isLastRow = idx >= days.length - 7;

                  const isDropTarget = dropTargetDay === day && dragEntry?.dateWorked !== `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                  return (
                    <div key={idx} onClick={() => day && openAddModal(day)}
                      onDragOver={day ? (e) => handleDragOver(day, e) : undefined}
                      onDragLeave={day ? handleDragLeave : undefined}
                      onDrop={day ? (e) => handleDrop(day, e) : undefined}
                      className={`border-r border-b border-slate-700/50 flex flex-col cursor-pointer transition-colors relative
                      ${!day ? 'bg-slate-900/20' : selectedResourceId ? 'bg-slate-900/40 hover:bg-slate-800/60' : 'bg-slate-900/40'}
                      ${idx % 7 === 6 ? 'border-r-0' : ''} ${isLastRow ? 'border-b-0' : ''}
                      ${isDropTarget ? 'ring-2 ring-inset ring-blue-500 bg-blue-900/20' : ''}`}>
                      {day && (
                        <>
                          <div className="flex items-center justify-between px-1.5 py-1">
                            <span className="text-[10px] text-slate-500">{dayEntries.length > 0 && `#${dayEntries.length}`}</span>
                            <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-slate-400'}`}>{day}</span>
                            <span className="text-[10px] text-slate-500">{dayHours > 0 && formatHours(dayHours)}</span>
                          </div>
                          <div className="px-1 space-y-0.5 overflow-hidden">
                            {visible.map((entry, i) => {
                              const color = getCompanyColor(entry.companyName);
                              const isDragging = dragEntry?.id === entry.id;
                              return (
                                <button key={i} onClick={(e) => { e.stopPropagation(); openEntryPopup(entry, e); }}
                                  draggable
                                  onDragStart={(e) => { e.stopPropagation(); handleDragStart(entry, e); }}
                                  onDragEnd={handleDragEnd}
                                  className={`w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate hover:bg-slate-700 text-slate-300 border-l-2 transition-colors cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''} bg-slate-700/60`}
                                  style={{ borderLeftColor: color.hex }}
                                  title={`${entry.companyName}: ${formatHours(entry.hoursWorked)} — drag to move/copy`}
                                >
                                  {formatHours(entry.hoursWorked)} {entry.companyName}
                                </button>
                              );
                            })}
                          </div>
                          {/* Mileage & Expense indicators for this day */}
                          {(() => {
                            const dayMileage = getMileageForDay(day);
                            const dayExpenses = getExpensesForDay(day);
                            if (dayMileage.length === 0 && dayExpenses.length === 0) return null;
                            return (
                              <div className="px-1 flex items-center gap-1 flex-wrap">
                                {dayMileage.map((m, mi) => (
                                  <span key={`m-${mi}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] bg-teal-900/40 text-teal-400 border border-teal-700/30" title={`${m.actualMiles} mi - ${m.startLocation} to ${m.destination}`}>
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                    {m.actualMiles}mi
                                  </span>
                                ))}
                                {dayExpenses.map((ex, ei) => (
                                  <span key={`e-${ei}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] bg-amber-900/40 text-amber-400 border border-amber-700/30" title={`${ex.item}: $${ex.amount.toFixed(2)}`}>
                                    $
                                    {ex.amount.toFixed(0)}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Bottom row: +more on left, + button on right - mt-auto pushes to bottom */}
                          <div className="mt-auto px-1 pb-0.5 flex items-center justify-between">
                            {overflow > 0 ? (
                              <button onClick={(e) => { e.stopPropagation(); openOverflowPopup(day, dayEntries, e); }}
                                className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors">
                                +{overflow} more
                              </button>
                            ) : <span />}
                            {selectedResourceId && (
                              <div className="flex items-center gap-0.5">
                                <button onClick={(e) => { e.stopPropagation(); openAddModal(day); }}
                                  className="w-4 h-4 rounded-full flex items-center justify-center text-slate-500 hover:text-blue-400 hover:bg-slate-700/50 transition-colors" title="Add entry">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            renderMainView()
          )}
        </div>
      </div>

      {/* OVERFLOW POPUP */}
      {overflowPopup && (
        <div className="fixed bg-slate-800 rounded-xl shadow-2xl border border-slate-600 w-96 max-h-80 overflow-hidden z-50" style={{ left: overflowPopup.x, top: overflowPopup.y }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-3 border-b border-slate-700">
            <span className="text-sm font-medium text-white">{currentDate.toLocaleDateString('en-US', { month: 'short' })} {overflowPopup.day} - {overflowPopup.entries.length} entries</span>
            <button onClick={() => setOverflowPopup(null)} className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="p-2 overflow-y-auto max-h-60 space-y-1">
            {overflowPopup.entries.map((entry, i) => {
              const color = getCompanyColor(entry.companyName);
              const isSelected = viewingEntry?.id === entry.id;
              const isDragging = dragEntry?.id === entry.id;
              return (
                <button key={i} onClick={(e) => openEntryPopup(entry, e)}
                  draggable
                  onDragStart={(e) => { e.stopPropagation(); handleDragStart(entry, e); requestAnimationFrame(() => setOverflowPopup(null)); }}
                  onDragEnd={handleDragEnd}
                  className={`w-full text-left px-3 py-2 rounded-md bg-slate-700/60 hover:bg-slate-700 border-l-2 transition-colors cursor-grab active:cursor-grabbing ${isSelected ? 'ring-2 ring-blue-500' : ''} ${isDragging ? 'opacity-40' : ''}`}
                  style={{ borderLeftColor: color.hex }}
                  title={`${entry.companyName}: ${formatHours(entry.hoursWorked)} — drag to move/copy`}>
                  <div className="font-medium text-sm text-slate-300">{formatHours(entry.hoursWorked)} - {entry.companyName}</div>
                  <div className="text-xs text-slate-500 truncate">{entry.summaryNotes}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ENTRY POPUP */}
      {viewingEntry && popupPosition && (
        <div className="fixed bg-slate-800 rounded-xl shadow-2xl border border-slate-600 w-80 z-[60]" style={{ left: popupPosition.x, top: popupPosition.y }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-2 border-b border-slate-700">
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-slate-400">View Entry</span>
              {(() => {
                const clientHours = companiesWithHours.find(c => c.name === normalizeCompanyName(viewingEntry.companyName));
                return clientHours ? (
                  <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">{clientHours.hours.toFixed(1)}h/mo</span>
                ) : null;
              })()}
            </div>
            <div className="flex items-center gap-1">
              {/* Edit button */}
              <button
                onClick={() => openEditModal(viewingEntry)}
                className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-blue-400"
                title="Edit entry"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              {/* Delete button */}
              <button
                onClick={() => {
                  if (confirm('Delete this time entry?')) {
                    handleDeleteEntry(viewingEntry);
                  }
                }}
                className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-red-400"
                title="Delete entry"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
              {/* Close button */}
              <button onClick={() => setViewingEntry(null)} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div className={`h-1 ${getCompanyColor(viewingEntry.companyName).bg}`} />
          <div className="p-3">
            <div className="text-lg font-medium text-white">{formatHours(viewingEntry.hoursWorked)}</div>
            <div className="text-sm text-slate-400 mb-2">{viewingEntry.companyName}</div>
            {viewingEntry.ticketNumber && (
              <div className="text-xs text-blue-400 font-mono mb-1">Ticket: {viewingEntry.ticketNumber}</div>
            )}
            {viewingEntry.ticketTitle && (
              <div className="text-xs text-slate-400 mb-2 italic">{viewingEntry.ticketTitle}</div>
            )}
            {viewingEntry.bidJobNumber && viewingEntry.bidJobDescription && (
              <div className="text-xs text-emerald-400 mb-2">Bid-Job: {viewingEntry.bidJobNumber} | {viewingEntry.bidJobDescription}</div>
            )}
            <div className="text-sm text-slate-300 mb-2">{viewingEntry.summaryNotes}</div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{viewingEntry.dateWorked}</span>
              <span>{viewingEntry.resourceName}</span>
            </div>
            {viewingEntry.source && (
              <div className="mt-2 text-xs text-purple-400">Source: {viewingEntry.source === 'timetech' ? 'TimeTech' : 'AutoTask'}</div>
            )}
          </div>
        </div>
      )}

      {/* EDIT ENTRY MODAL */}
      {editingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => setEditingEntry(null)}>
          <div className="bg-slate-800 rounded-xl border border-slate-600 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                <span className="text-base font-medium text-white">Edit Time Entry</span>
                {(() => {
                  const clientHours = companiesWithHours.find(c => c.name === normalizeCompanyName(editingEntry.companyName));
                  return clientHours ? (
                    <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded-full">{clientHours.name}: {clientHours.hours.toFixed(1)}h this month</span>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center gap-1">
                {/* Delete button */}
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                  title="Delete entry"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
                {/* Close button */}
                <button onClick={() => setEditingEntry(null)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Warning - edits are local only */}
              <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3 text-sm text-amber-300">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  <span>Changes are local only and won't sync to {editingEntry.source === 'timetech' ? 'TimeTech' : 'AutoTask'}.</span>
                </div>
              </div>

              {editError && (
                <div className="text-red-400 text-sm bg-red-900/30 rounded-lg p-3">{editError}</div>
              )}

              {/* Entry info (read-only) */}
              <div className="bg-slate-700/50 rounded-lg p-3 space-y-1">
                <div className="text-sm text-slate-300">{editingEntry.companyName}</div>
                {editingEntry.ticketNumber && (
                  <div className="text-xs text-blue-400 font-mono">Ticket: {editingEntry.ticketNumber}</div>
                )}
                {editingEntry.ticketTitle && (
                  <div className="text-xs text-slate-400">{editingEntry.ticketTitle}</div>
                )}
                <div className="text-xs text-slate-500">{editingEntry.resourceName}</div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Date *</label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Hours */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Hours *</label>
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                  className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Description *</label>
                <textarea
                  value={editSummary}
                  onChange={(e) => setEditSummary(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none resize-none"
                />
              </div>

              {/* Bid-Job */}
              {editBidJobs.length > 0 && (
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Bid-Job</label>
                  <select
                    value={editSelectedBidJob?.id || ''}
                    onChange={(e) => {
                      const bidJob = editBidJobs.find(bj => bj.id === e.target.value);
                      setEditSelectedBidJob(bidJob || null);
                    }}
                    className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">None</option>
                    {editBidJobs.map(bj => (
                      <option key={bj.id} value={bj.id}>{bj.bidJobNumber} | {bj.bidJobDescription}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Delete confirmation (shown when delete clicked in header) */}
              {deleteConfirm && (
                <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3">
                  <p className="text-sm text-red-300 mb-3">Are you sure you want to delete this entry? This cannot be undone.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDeleteConfirm(false)}
                      className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors"
                    >
                      Keep
                    </button>
                    <button
                      onClick={() => handleDeleteEntry()}
                      disabled={editLoading}
                      className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm transition-colors disabled:opacity-50"
                    >
                      {editLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {!deleteConfirm && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditingEntry(null)}
                    className="flex-1 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editLoading || !editHours || !editSummary.trim()}
                    className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  >
                    {editLoading && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>}
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DRAG MOVE/COPY MODAL */}
      {dragActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => !dragActionLoading && setDragActionModal(null)}>
          <div className="bg-slate-800 rounded-xl border border-slate-600 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-700">
              <span className="text-base font-medium text-white">Move or Copy?</span>
            </div>
            <div className="p-4 space-y-3">
              {/* Entry info */}
              <div className="bg-slate-700/50 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{formatHours(dragActionModal.entry.hoursWorked)}</span>
                  <span className="text-sm text-slate-400">—</span>
                  <span className="text-sm text-slate-300">{dragActionModal.entry.companyName}</span>
                </div>
                {dragActionModal.entry.summaryNotes && (
                  <div className="text-xs text-slate-400 truncate">{dragActionModal.entry.summaryNotes}</div>
                )}
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                  <span>{dragActionModal.entry.dateWorked}</span>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  <span className="text-blue-400">{dragActionModal.targetDate}</span>
                </div>
              </div>

              {dragActionError && (
                <div className="text-red-400 text-sm bg-red-900/30 rounded-lg p-3">{dragActionError}</div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setDragActionModal(null)}
                  disabled={dragActionLoading}
                  className="flex-1 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDragMove}
                  disabled={dragActionLoading}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {dragActionLoading ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : null}
                  Move
                </button>
                <button
                  onClick={handleDragCopy}
                  disabled={dragActionLoading}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {dragActionLoading ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : null}
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD ENTRY MODAL */}
      {showAddModal && modalDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => setShowAddModal(false)}>
          <div className={`bg-slate-800 rounded-xl border border-slate-600 w-full shadow-2xl max-h-[90vh] overflow-y-auto ${batchTickets.length > 1 ? 'max-w-2xl' : 'max-w-md'}`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <span className="text-base font-medium text-white">Add Entry</span>
                <div className="flex items-center gap-1 text-slate-400">
                  <button onClick={() => setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth(), modalDate.getDate() - 1))} className="p-1 hover:bg-slate-700 rounded">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span className="text-sm min-w-[80px] text-center">{modalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <button onClick={() => setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth(), modalDate.getDate() + 1))} className="p-1 hover:bg-slate-700 rounded">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Entry Type Tabs */}
            <div className="flex border-b border-slate-700">
              <button type="button" onClick={() => setEntryType('time')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${entryType === 'time' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-slate-300'}`}>
                Time
              </button>
              <button type="button" onClick={() => setEntryType('mileage')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${entryType === 'mileage' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-400 hover:text-slate-300'}`}>
                Mileage
              </button>
              <button type="button" onClick={() => setEntryType('expense')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${entryType === 'expense' ? 'text-amber-400 border-b-2 border-amber-400' : 'text-slate-400 hover:text-slate-300'}`}>
                Expense
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-4 space-y-4">
              {formError && <div className="text-red-400 text-sm bg-red-900/30 rounded-lg p-3">{formError}</div>}

              {/* Company Selector */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Company *</label>
                {selectedCompany ? (
                  <div className="bg-slate-700 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{selectedCompany.companyName}</span>
                      {selectedCompany.source === 'manual' && (
                        <span className="px-1.5 py-0.5 text-xs bg-violet-600/30 text-violet-300 rounded">Custom</span>
                      )}
                    </div>
                    <button type="button" onClick={() => { setSelectedCompany(null); setCompanySearch(''); setSelectedTicket(null); setIsCreatingNewTicket(false); }} className="text-slate-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input ref={companySearchRef} type="text" value={companySearch}
                      onChange={e => { setCompanySearch(e.target.value); setHighlightedCompanyIndex(-1); }}
                      onKeyDown={e => {
                        if (companySearch.length < 2 || filteredCompanies.length === 0) return;
                        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedCompanyIndex(p => Math.min(p + 1, filteredCompanies.length - 1)); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedCompanyIndex(p => Math.max(p - 1, 0)); }
                        else if (e.key === 'Enter' && highlightedCompanyIndex >= 0) { e.preventDefault(); setSelectedCompany(filteredCompanies[highlightedCompanyIndex]); setCompanySearch(''); setHighlightedCompanyIndex(-1); }
                        else if (e.key === 'Escape') { setCompanySearch(''); setHighlightedCompanyIndex(-1); }
                      }}
                      placeholder="Search companies..." className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none" />
                    {companySearch.length >= 2 && (
                      <div ref={companyDropdownRef} className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredCompanies.map((c, i) => (
                          <button key={c.id} type="button" onClick={() => { setSelectedCompany(c); setCompanySearch(''); setHighlightedCompanyIndex(-1); }}
                            className={`w-full text-left p-3 text-sm flex items-center gap-2 ${i === highlightedCompanyIndex ? 'bg-blue-600 text-white' : 'text-white hover:bg-slate-600'}`}
                          >
                            {c.companyName}
                            {c.source === 'manual' && (
                              <span className="px-1.5 py-0.5 text-xs bg-violet-600/30 text-violet-300 rounded">Custom</span>
                            )}
                          </button>
                        ))}
                        {filteredCompanies.length === 0 && <div className="p-3 text-sm text-slate-400">No results</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* === TIME ENTRY FIELDS === */}
              {entryType === 'time' && (
                <>
                  {/* Ticket Selector - Hidden for custom clients */}
                  {selectedCompany?.source !== 'manual' && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">
                        Tickets * {batchTickets.length > 0 && <span className="text-blue-400 ml-1">({batchTickets.length} selected)</span>}
                      </label>
                      {!selectedCompany ? (
                        <div className="text-sm text-slate-500 py-2 bg-slate-700/50 rounded px-3">Select a company first</div>
                      ) : loadingTickets ? (
                        <div className="text-sm text-slate-500 py-2">Loading tickets...</div>
                      ) : (
                        <div className="border border-slate-600 rounded-lg overflow-hidden">
                          {/* Create New Ticket option */}
                          <button type="button"
                            onClick={() => {
                              if (isCreatingNewTicket) {
                                setIsCreatingNewTicket(false);
                              } else {
                                setIsCreatingNewTicket(true);
                                setBatchTickets([]);
                                setSelectedTicket(null);
                              }
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b border-slate-600 ${isCreatingNewTicket ? 'bg-green-900/30 text-green-300' : 'bg-slate-700 text-green-400 hover:bg-slate-600'}`}
                          >
                            <span className="text-green-400 font-bold">+</span> Create New Ticket
                          </button>

                          {/* Search filter */}
                          {!isCreatingNewTicket && companyTickets.length > 5 && (
                            <div className="px-2 py-1.5 bg-slate-750 border-b border-slate-600">
                              <input
                                type="text"
                                value={ticketSearchFilter}
                                onChange={(e) => setTicketSearchFilter(e.target.value)}
                                placeholder="Filter tickets..."
                                className="w-full bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                          )}

                          {/* Scrollable checkbox list with inline hours/close */}
                          {!isCreatingNewTicket && (
                            <div className="max-h-56 overflow-y-auto bg-slate-800">
                              {(() => {
                                const filterLower = ticketSearchFilter.toLowerCase();
                                const filtered = companyTickets.filter(t =>
                                  !ticketSearchFilter ||
                                  t.ticketNumber.toLowerCase().includes(filterLower) ||
                                  t.title.toLowerCase().includes(filterLower)
                                );
                                const isCompleted = (t: Ticket) => t.status === 'Complete' || t.statusId === 5;
                                const newUnassigned = filtered.filter(t => !isCompleted(t) && (t.status === 'New' || !t.assignedResourceId));
                                const active = filtered.filter(t => !isCompleted(t) && t.status !== 'New' && t.assignedResourceId);
                                // Closed: limit to 20 most recent
                                const closed = filtered.filter(t => isCompleted(t)).slice(0, 20);
                                const isBatchMode = batchTickets.length > 1;

                                const renderTicketRow = (ticket: Ticket, highlight?: boolean) => {
                                  const batchEntry = batchTickets.find(bt => bt.ticket.autoTaskId === ticket.autoTaskId);
                                  const isChecked = !!batchEntry;
                                  return (
                                    <div key={ticket.id} className={`px-3 py-1.5 hover:bg-slate-700 ${isChecked && isBatchMode ? 'bg-slate-700/60 pb-2' : isChecked ? 'bg-slate-700/60' : ''}`}>
                                      <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleBatchTicket(ticket)}
                                            className="w-3.5 h-3.5 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                          />
                                          <span className={`text-sm truncate ${highlight ? 'text-red-400' : 'text-white'}`}>
                                            {ticket.ticketNumber} - {ticket.title.substring(0, isBatchMode ? 28 : 35)}{ticket.title.length > (isBatchMode ? 28 : 35) ? '...' : ''}
                                          </span>
                                        </label>
                                        {/* Inline hours + close when checked and in batch mode */}
                                        {isChecked && isBatchMode && (
                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <input
                                              type="number"
                                              step="0.25"
                                              min="0.25"
                                              max="24"
                                              value={batchEntry!.hoursWorked}
                                              onChange={(e) => updateBatchEntry(ticket.autoTaskId, { hoursWorked: e.target.value })}
                                              placeholder="Hrs"
                                              onClick={(e) => e.stopPropagation()}
                                              className="w-16 bg-slate-700 border border-slate-600 text-white text-xs rounded px-1.5 py-1 focus:border-blue-500 focus:outline-none"
                                            />
                                            {ticket.status !== 'Complete' && ticket.statusId !== 5 && (
                                              <label className="flex items-center gap-0.5 cursor-pointer" title="Close ticket" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                  type="checkbox"
                                                  checked={batchEntry!.closeTicket}
                                                  onChange={(e) => updateBatchEntry(ticket.autoTaskId, { closeTicket: e.target.checked })}
                                                  className="w-3 h-3 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-[10px] text-slate-400">Close</span>
                                              </label>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      {/* Per-ticket description in batch mode */}
                                      {isChecked && isBatchMode && (
                                        <div className="mt-1 ml-5.5 pl-0.5">
                                          <textarea
                                            value={batchEntry!.summaryNotes}
                                            onChange={(e) => updateBatchEntry(ticket.autoTaskId, { summaryNotes: e.target.value })}
                                            placeholder="What did you work on for this ticket?"
                                            rows={2}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full bg-slate-600 border border-slate-500 text-white text-xs rounded px-2 py-1 focus:border-blue-500 focus:outline-none resize-none"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                };

                                return (
                                  <>
                                    {newUnassigned.length > 0 && (
                                      <>
                                        <div className="px-3 py-1 text-xs text-slate-500 font-medium bg-slate-800 sticky top-0">New / Unassigned</div>
                                        {newUnassigned.map(t => renderTicketRow(t, true))}
                                      </>
                                    )}
                                    {active.length > 0 && (
                                      <>
                                        <div className="px-3 py-1 text-xs text-slate-500 font-medium bg-slate-800 sticky top-0">Active Tickets</div>
                                        {active.map(t => renderTicketRow(t))}
                                      </>
                                    )}
                                    {closed.length > 0 && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setShowClosedTickets(prev => !prev)}
                                          className="w-full px-3 py-1 text-xs text-slate-500 font-medium bg-slate-800 sticky top-0 flex items-center gap-1 hover:text-slate-300"
                                        >
                                          <svg className={`w-3 h-3 transition-transform ${showClosedTickets ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                          Closed ({closed.length}{closed.length === 20 ? '+' : ''})
                                        </button>
                                        {showClosedTickets && closed.map(t => renderTicketRow(t))}
                                      </>
                                    )}
                                    {filtered.length === 0 && (
                                      <div className="px-3 py-3 text-sm text-slate-500 text-center">No tickets found</div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {/* Batch total */}
                          {batchTickets.length > 1 && !isCreatingNewTicket && (
                            <div className="px-3 py-1.5 text-xs text-slate-400 text-right border-t border-slate-600 bg-slate-800">
                              Total: {batchTickets.reduce((sum, bt) => sum + (parseFloat(bt.hoursWorked) || 0), 0).toFixed(2)}h across {batchTickets.length} tickets
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Batch results display (partial failure) */}
                  {batchResults && batchResults.failCount > 0 && (
                    <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3 space-y-1">
                      <p className="text-sm text-red-300 font-medium">Batch Results: {batchResults.successCount} saved, {batchResults.failCount} failed</p>
                      {batchResults.results.filter(r => !r.success).map(r => (
                        <p key={r.ticketId} className="text-xs text-red-400">{r.ticketNumber}: {r.error}</p>
                      ))}
                    </div>
                  )}

                  {/* Silent close failures (entry saved, but close didn't apply) */}
                  {batchResults && batchResults.results.some(r => r.success && r.closeRequested && !r.closedTicket) && (
                    <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3 space-y-1">
                      <p className="text-sm text-amber-300 font-medium">Some tickets didn't close</p>
                      <p className="text-xs text-amber-400">Time entries saved, but AutoTask rejected the close for these tickets. Close manually if still needed.</p>
                      {batchResults.results.filter(r => r.success && r.closeRequested && !r.closedTicket).map(r => (
                        <p key={r.ticketId} className="text-xs text-amber-400">{r.ticketNumber}{r.closeError ? `: ${r.closeError}` : ''}</p>
                      ))}
                    </div>
                  )}

                  {/* Close ticket checkbox (single-ticket mode only) */}
                  {batchTickets.length === 1 && !isCreatingNewTicket && selectedCompany?.source !== 'manual' &&
                   batchTickets[0].ticket.status !== 'Complete' && batchTickets[0].ticket.statusId !== 5 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={closeExistingTicket} onChange={(e) => setCloseExistingTicket(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-slate-300">Close ticket after saving</span>
                    </label>
                  )}

                  {/* Info message for custom clients */}
                  {selectedCompany?.source === 'manual' && (
                    <div className="bg-violet-900/20 border border-violet-600/30 rounded-lg p-3">
                      <p className="text-sm text-violet-300">
                        Custom clients don't have AutoTask tickets. Time entries will be tracked locally only.
                      </p>
                    </div>
                  )}

                  {/* New Ticket Fields */}
                  {isCreatingNewTicket && selectedCompany?.source !== 'manual' && (
                    <>
                      <div>
                        <label className="block text-sm text-slate-400 mb-1.5">Ticket Title *</label>
                        <input type="text" value={newTicketTitle} onChange={(e) => setNewTicketTitle(e.target.value)} placeholder="Enter ticket title..."
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-slate-400 mb-1.5">Queue</label>
                          <select value={newTicketQueue} onChange={(e) => setNewTicketQueue(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:border-blue-500 focus:outline-none">
                            <option value="Level I Support">Level I Support</option>
                            <option value="Level II Support">Level II Support</option>
                            <option value="Projects">Projects</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-1.5">Priority</label>
                          <select value={newTicketPriority} onChange={(e) => setNewTicketPriority(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:border-blue-500 focus:outline-none">
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={autoCompleteTicket} onChange={(e) => setAutoCompleteTicket(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm text-slate-300">Complete ticket after saving</span>
                      </label>
                    </>
                  )}

                  {/* Hours (hidden in batch mode — each ticket has its own hours) */}
                  {batchTickets.length <= 1 && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Hours *</label>
                      <input type="number" step="0.25" min="0.25" max="24" value={hoursWorked} onChange={(e) => setHoursWorked(e.target.value)} placeholder="0.00"
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none" />
                    </div>
                  )}

                  {/* Bid-Job */}
                  {(loadingBidJobs || bidJobs.length > 0) && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Bid-Job</label>
                      {loadingBidJobs ? (
                        <div className="text-slate-500 text-sm">Loading bid-jobs...</div>
                      ) : (
                        <select
                          value={selectedBidJob?.id || ''}
                          onChange={(e) => {
                            const bidJob = bidJobs.find(bj => bj.id === e.target.value);
                            setSelectedBidJob(bidJob || null);
                          }}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Select bid-job...</option>
                          {bidJobs.map(bj => (
                            <option key={bj.id} value={bj.id}>
                              {bj.bidJobNumber} | {bj.bidJobDescription}
                              {bj.isDefault ? ' (Default)' : ''}
                              {bj.isCommon ? ' [Common]' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Description (hidden in batch mode — each ticket has its own) */}
                  {batchTickets.length <= 1 && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Description *</label>
                      <textarea value={summaryNotes} onChange={(e) => setSummaryNotes(e.target.value)} placeholder="What did you work on?" rows={3}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none resize-none" />
                    </div>
                  )}
                </>
              )}

              {/* === MILEAGE ENTRY FIELDS === */}
              {entryType === 'mileage' && (
                <>
                  {/* Ticket (optional for mileage) */}
                  {selectedCompany && selectedCompany.source !== 'manual' && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Ticket (optional)</label>
                      {loadingTickets ? (
                        <div className="text-sm text-slate-500 py-2">Loading tickets...</div>
                      ) : (
                        <select value={selectedTicket?.id || ''}
                          onChange={(e) => {
                            if (e.target.value) setSelectedTicket(companyTickets.find(t => t.id === e.target.value) || null);
                            else setSelectedTicket(null);
                          }}
                          className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none">
                          <option value="">No ticket</option>
                          {companyTickets.map(ticket => (
                            <option key={ticket.id} value={ticket.id}>
                              {ticket.ticketNumber} - {ticket.title.substring(0, 40)}{ticket.title.length > 40 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Start Location */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Start</label>
                    <select value={calMileageStart} onChange={(e) => setCalMileageStart(e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:border-teal-500 focus:outline-none">
                      <option value="Office">Office</option>
                      {selectedCompany && <option value={selectedCompany.companyName}>{selectedCompany.companyName}</option>}
                      <option value="Other">Other</option>
                    </select>
                    {calMileageStart === 'Other' && (
                      <input type="text" placeholder="Enter start location..."
                        onChange={(e) => setCalMileageStart(e.target.value || 'Other')}
                        className="mt-1 w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:border-teal-500 focus:outline-none" />
                    )}
                  </div>

                  {/* Destination */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Destination</label>
                    <input type="text" value={calMileageDestination || selectedCompany?.companyName || ''}
                      onChange={(e) => setCalMileageDestination(e.target.value)}
                      placeholder="Destination..."
                      className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:border-teal-500 focus:outline-none" />
                  </div>

                  {/* Estimated distance */}
                  {calEstimatedMiles && (
                    <div className="text-xs text-teal-400 bg-teal-900/20 rounded px-3 py-2">
                      Approx distance: {calEstimatedMiles.toFixed(1)} mi
                    </div>
                  )}

                  {/* Odometer */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Odometer Start *</label>
                      <input type="number" step="0.1" value={calOdometerStart} onChange={(e) => setCalOdometerStart(e.target.value)}
                        placeholder="0" className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-teal-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Odometer End *</label>
                      <input type="number" step="0.1" value={calOdometerEnd} onChange={(e) => setCalOdometerEnd(e.target.value)}
                        placeholder="0" className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-teal-500 focus:outline-none" />
                    </div>
                  </div>

                  {/* Actual Miles + Warning */}
                  {calOdometerStart && calOdometerEnd && parseFloat(calOdometerEnd) > parseFloat(calOdometerStart) && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-300">Actual: <span className="text-white font-medium">{(parseFloat(calOdometerEnd) - parseFloat(calOdometerStart)).toFixed(1)} mi</span></span>
                      {calEstimatedMiles && Math.abs((parseFloat(calOdometerEnd) - parseFloat(calOdometerStart)) - calEstimatedMiles) / calEstimatedMiles > 0.2 && (
                        <span className="text-xs text-amber-400 bg-amber-900/30 rounded px-2 py-0.5">20%+ off estimate</span>
                      )}
                      <span className="text-xs text-slate-500 ml-auto">
                        ${((parseFloat(calOdometerEnd) - parseFloat(calOdometerStart)) * calMileageRate).toFixed(2)} reimbursement
                      </span>
                    </div>
                  )}

                  {/* Item (optional) */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Item / Note</label>
                    <input type="text" value={calMileageItem} onChange={(e) => setCalMileageItem(e.target.value)}
                      placeholder="e.g., Drive to client site"
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-teal-500 focus:outline-none" />
                  </div>
                </>
              )}

              {/* === EXPENSE ENTRY FIELDS === */}
              {entryType === 'expense' && (
                <>
                  {/* Ticket (optional for expenses) */}
                  {selectedCompany && selectedCompany.source !== 'manual' && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1.5">Ticket (optional)</label>
                      {loadingTickets ? (
                        <div className="text-sm text-slate-500 py-2">Loading tickets...</div>
                      ) : (
                        <select value={selectedTicket?.id || ''}
                          onChange={(e) => {
                            if (e.target.value) setSelectedTicket(companyTickets.find(t => t.id === e.target.value) || null);
                            else setSelectedTicket(null);
                          }}
                          className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:border-amber-500 focus:outline-none">
                          <option value="">No ticket</option>
                          {companyTickets.map(ticket => (
                            <option key={ticket.id} value={ticket.id}>
                              {ticket.ticketNumber} - {ticket.title.substring(0, 40)}{ticket.title.length > 40 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Item */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Item *</label>
                    <input type="text" value={calExpenseItem} onChange={(e) => setCalExpenseItem(e.target.value)}
                      placeholder="e.g., Parking, Education, Supplies..."
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-amber-500 focus:outline-none" />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                      <input type="number" step="0.01" min="0.01" value={calExpenseAmount} onChange={(e) => setCalExpenseAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg pl-7 pr-3 py-2.5 focus:border-amber-500 focus:outline-none" />
                    </div>
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">Cancel</button>
                <button type="submit" disabled={formSubmitting || (
                  entryType === 'time' ? (
                    !selectedCompany ||
                    (batchTickets.length <= 1 && !summaryNotes.trim()) ||
                    (batchTickets.length > 1 && batchTickets.some(bt => !bt.summaryNotes.trim())) ||
                    (selectedCompany?.source !== 'manual' && batchTickets.length === 0 && !selectedTicket && !isCreatingNewTicket) ||
                    (batchTickets.length <= 1 && !hoursWorked) ||
                    (batchTickets.length > 1 && batchTickets.some(bt => !bt.hoursWorked || parseFloat(bt.hoursWorked) <= 0)) ||
                    (isCreatingNewTicket && selectedCompany?.source !== 'manual' && !newTicketTitle.trim())
                  ) :
                  entryType === 'mileage' ? (!calOdometerStart || !calOdometerEnd || parseFloat(calOdometerEnd) <= parseFloat(calOdometerStart)) :
                  entryType === 'expense' ? (!calExpenseItem.trim() || !calExpenseAmount || parseFloat(calExpenseAmount) <= 0) : true
                )}
                  className={`flex-1 py-2.5 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors ${
                    entryType === 'mileage' ? 'bg-teal-600 hover:bg-teal-700' :
                    entryType === 'expense' ? 'bg-amber-600 hover:bg-amber-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}>
                  {formSubmitting && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>}
                  {formSubmitting ? 'Saving...' :
                    entryType === 'time' ? (batchTickets.length > 1 ? `Save ${batchTickets.length} Entries` : 'Save Entry') :
                    entryType === 'mileage' ? 'Save Mileage' : 'Save Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      <NewTicketModal isOpen={showNewTicketModal} onClose={() => setShowNewTicketModal(false)} onTicketCreated={() => {}} />

      {showPendingModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowPendingModal(false)}>
          <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b ${pendingModalType === 'pending' ? 'border-yellow-600/50' : 'border-red-600/50'}`}>
              <h2 className={`text-lg font-medium ${pendingModalType === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>
                {pendingModalType === 'pending' ? 'Pending' : 'Failed'} Entries
              </h2>
              <button onClick={() => setShowPendingModal(false)} className="text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const entries = monthData?.entries?.filter(e => e.syncStatus === pendingModalType) || [];
                if (entries.length === 0) return <div className="text-slate-400 text-center py-4">No {pendingModalType} entries</div>;
                return (
                  <div className="space-y-2">
                    <div className="text-sm text-slate-400 mb-2">{entries.length} {pendingModalType} {entries.length === 1 ? 'entry' : 'entries'}</div>
                    {entries.map(e => (
                      <div key={e.id} className="bg-slate-900 border border-slate-600 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-slate-300 font-medium">{e.dateWorked}</span>
                              <span className="text-slate-500">|</span>
                              <span className="text-slate-400">{e.ticketNumber}</span>
                              <span className="text-slate-500">|</span>
                              <span className="text-blue-400">{e.hoursWorked}h</span>
                            </div>
                            <div className="text-xs text-slate-400 mt-1 truncate">{e.companyName} - {e.resourceName}</div>
                            {e.summaryNotes && <div className="text-xs text-slate-500 mt-1 truncate">{e.summaryNotes}</div>}
                            {e.syncError && <div className="text-xs text-red-400 mt-1">{e.syncError}</div>}
                          </div>
                          {pendingModalType === 'failed' && (
                            <button
                              onClick={async () => {
                                setRetryingEntries(prev => new Set(prev).add(e.id));
                                try {
                                  await retryTimeTechSync(e.id);
                                  addToast('success', `TimeTech sync succeeded for ${e.ticketNumber}`);
                                  await loadCalendarData();
                                } catch (err) {
                                  addToast('error', `Retry failed: ${err instanceof Error ? err.message : String(err)}`);
                                } finally {
                                  setRetryingEntries(prev => { const next = new Set(prev); next.delete(e.id); return next; });
                                }
                              }}
                              disabled={retryingEntries.has(e.id)}
                              className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {retryingEntries.has(e.id) ? 'Retrying...' : 'Retry'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="p-4 border-t border-slate-700 flex gap-2">
              {pendingModalType === 'failed' && (monthData?.entries?.filter(e => e.syncStatus === 'failed').length || 0) > 0 && (
                <button
                  onClick={async () => {
                    const failedEntries = monthData?.entries?.filter(e => e.syncStatus === 'failed') || [];
                    const ids = failedEntries.map(e => e.id);
                    setRetryingEntries(new Set(ids));
                    let successCount = 0;
                    for (const entry of failedEntries) {
                      try {
                        await retryTimeTechSync(entry.id);
                        successCount++;
                      } catch { /* individual failures handled by per-entry state */ }
                    }
                    setRetryingEntries(new Set());
                    if (successCount > 0) {
                      addToast('success', `${successCount} of ${failedEntries.length} entries synced to TimeTech`);
                      await loadCalendarData();
                    } else {
                      addToast('error', 'All retry attempts failed');
                    }
                  }}
                  disabled={retryingEntries.size > 0}
                  className="flex-1 py-2 px-4 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors"
                >
                  {retryingEntries.size > 0 ? 'Retrying All...' : 'Retry All'}
                </button>
              )}
              <button onClick={() => setShowPendingModal(false)} className="flex-1 py-2 px-4 rounded-lg text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-300">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
